import { getCSRFToken } from '../utils/getCSRFToken';
import {
  calculateNights,
  calculateDaysInclusive,
  parseDate,
} from '../utils/dateUtils';
import { mountStripePaymentElement } from '../utils/mountStripePaymentElement';
import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';

// Place this at the top of the file, outside the Alpine.store definition
function handleExpiredSession(redirectUrl) {
  // Clear all session sources
  sessionStorage.removeItem('bookingSessionId');
  localStorage.removeItem('bookingSessionId');
  localStorage.removeItem('lastActiveTime');
  localStorage.removeItem('sessionExpiredMessage');
  localStorage.removeItem('lastExpiredMessageTime');
  document.cookie =
    'bookingSessionId=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

  // Immediately redirect to homepage with cleared cart
  const store = Alpine.store('storeCartStore');
  if (store) {
    // Reset the store state
    store.resetStore();
    store.createNewSession();
  }

  // Use the calendar slug from the store if available, otherwise extract from URL
  let calendarSlug = null;
  if (store && store.calendarSlug) {
    calendarSlug = store.calendarSlug;
  } else {
    // Extract calendar slug from current URL as fallback
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/');
    calendarSlug = pathParts[2]; // Extract from /bookings/[slug]/...
  }

  if (calendarSlug && calendarSlug !== 'null' && calendarSlug !== 'home') {
    window.location.href = `/bookings/${calendarSlug}/home/?session_expired=1`;
  } else {
    // Last resort: refresh the current page
    window.location.reload();
  }
}

// Phone Number Component for checkout page
function phoneNumberComponent() {
  return {
    intCodes: [
      {
        name: 'United States',
        dial_code: '+1',
        code: 'US',
        flag: '🇺🇸',
        format: '(999) 999-9999',
      },
      {
        name: 'United Kingdom',
        dial_code: '+44',
        code: 'GB',
        flag: '🇬🇧',
        format: '9999 999 9999',
      },
      {
        name: 'France',
        dial_code: '+33',
        code: 'FR',
        flag: '🇫🇷',
        format: '9 99 99 99 99',
      },
      {
        name: 'Germany',
        dial_code: '+49',
        code: 'DE',
        flag: '🇩🇪',
        format: '999 99999999',
      },
      {
        name: 'Italy',
        dial_code: '+39',
        code: 'IT',
        flag: '🇮🇹',
        format: '999 999 9999',
      },
      {
        name: 'Spain',
        dial_code: '+34',
        code: 'ES',
        flag: '🇪🇸',
        format: '999 99 99 99',
      },
      {
        name: 'Netherlands',
        dial_code: '+31',
        code: 'NL',
        flag: '🇳🇱',
        format: '9 9999 9999',
      },
      {
        name: 'Belgium',
        dial_code: '+32',
        code: 'BE',
        flag: '🇧🇪',
        format: '999 99 99 99',
      },
      {
        name: 'Switzerland',
        dial_code: '+41',
        code: 'CH',
        flag: '🇨🇭',
        format: '99 999 99 99',
      },
      {
        name: 'Austria',
        dial_code: '+43',
        code: 'AT',
        flag: '🇦🇹',
        format: '999 999999',
      },
      {
        name: 'Denmark',
        dial_code: '+45',
        code: 'DK',
        flag: '🇩🇰',
        format: '99 99 99 99',
      },
      {
        name: 'Sweden',
        dial_code: '+46',
        code: 'SE',
        flag: '🇸🇪',
        format: '99-999 99 99',
      },
      {
        name: 'Norway',
        dial_code: '+47',
        code: 'NO',
        flag: '🇳🇴',
        format: '999 99 999',
      },
      {
        name: 'Finland',
        dial_code: '+358',
        code: 'FI',
        flag: '🇫🇮',
        format: '99 999 9999',
      },
      {
        name: 'Portugal',
        dial_code: '+351',
        code: 'PT',
        flag: '🇵🇹',
        format: '999 999 999',
      },
      {
        name: 'Greece',
        dial_code: '+30',
        code: 'GR',
        flag: '🇬🇷',
        format: '999 999 9999',
      },
      {
        name: 'Poland',
        dial_code: '+48',
        code: 'PL',
        flag: '🇵🇱',
        format: '999 999 999',
      },
      {
        name: 'Czech Republic',
        dial_code: '+420',
        code: 'CZ',
        flag: '🇨🇿',
        format: '999 999 999',
      },
      {
        name: 'Slovakia',
        dial_code: '+421',
        code: 'SK',
        flag: '🇸🇰',
        format: '999 999 999',
      },
      {
        name: 'Hungary',
        dial_code: '+36',
        code: 'HU',
        flag: '🇭🇺',
        format: '99 999 9999',
      },
      {
        name: 'Romania',
        dial_code: '+40',
        code: 'RO',
        flag: '🇷🇴',
        format: '999 999 999',
      },
      {
        name: 'Bulgaria',
        dial_code: '+359',
        code: 'BG',
        flag: '🇧🇬',
        format: '999 999 999',
      },
      {
        name: 'Croatia',
        dial_code: '+385',
        code: 'HR',
        flag: '🇭🇷',
        format: '99 999 9999',
      },
      {
        name: 'Slovenia',
        dial_code: '+386',
        code: 'SI',
        flag: '🇸🇮',
        format: '99 999 999',
      },
      {
        name: 'Estonia',
        dial_code: '+372',
        code: 'EE',
        flag: '🇪🇪',
        format: '9999 9999',
      },
      {
        name: 'Latvia',
        dial_code: '+371',
        code: 'LV',
        flag: '🇱🇻',
        format: '9999 9999',
      },
      {
        name: 'Lithuania',
        dial_code: '+370',
        code: 'LT',
        flag: '🇱🇹',
        format: '999 99999',
      },
      {
        name: 'Russia',
        dial_code: '+7',
        code: 'RU',
        flag: '🇷🇺',
        format: '999 999 99 99',
      },
      {
        name: 'Ukraine',
        dial_code: '+380',
        code: 'UA',
        flag: '🇺🇦',
        format: '99 999 99 99',
      },
      {
        name: 'Belarus',
        dial_code: '+375',
        code: 'BY',
        flag: '🇧🇾',
        format: '99 999-99-99',
      },
      {
        name: 'Moldova',
        dial_code: '+373',
        code: 'MD',
        flag: '🇲🇩',
        format: '999 99 999',
      },
      {
        name: 'Japan',
        dial_code: '+81',
        code: 'JP',
        flag: '🇯🇵',
        format: '99-9999-9999',
      },
      {
        name: 'China',
        dial_code: '+86',
        code: 'CN',
        flag: '🇨🇳',
        format: '999 9999 9999',
      },
      {
        name: 'South Korea',
        dial_code: '+82',
        code: 'KR',
        flag: '🇰🇷',
        format: '99 9999 9999',
      },
      {
        name: 'Singapore',
        dial_code: '+65',
        code: 'SG',
        flag: '🇸🇬',
        format: '9999 9999',
      },
      {
        name: 'Malaysia',
        dial_code: '+60',
        code: 'MY',
        flag: '🇲🇾',
        format: '99-999 9999',
      },
      {
        name: 'Thailand',
        dial_code: '+66',
        code: 'TH',
        flag: '🇹🇭',
        format: '99 999 9999',
      },
      {
        name: 'Vietnam',
        dial_code: '+84',
        code: 'VN',
        flag: '🇻🇳',
        format: '999 999 9999',
      },
      {
        name: 'Philippines',
        dial_code: '+63',
        code: 'PH',
        flag: '🇵🇭',
        format: '999 999 9999',
      },
      {
        name: 'Indonesia',
        dial_code: '+62',
        code: 'ID',
        flag: '🇮🇩',
        format: '999-9999-9999',
      },
      {
        name: 'India',
        dial_code: '+91',
        code: 'IN',
        flag: '🇮🇳',
        format: '99999 99999',
      },
      {
        name: 'Pakistan',
        dial_code: '+92',
        code: 'PK',
        flag: '🇵🇰',
        format: '999 9999999',
      },
      {
        name: 'Bangladesh',
        dial_code: '+880',
        code: 'BD',
        flag: '🇧🇩',
        format: '9999 999999',
      },
      {
        name: 'Sri Lanka',
        dial_code: '+94',
        code: 'LK',
        flag: '🇱🇰',
        format: '99 999 9999',
      },
      {
        name: 'Nepal',
        dial_code: '+977',
        code: 'NP',
        flag: '🇳🇵',
        format: '999-9999999',
      },
      {
        name: 'Bhutan',
        dial_code: '+975',
        code: 'BT',
        flag: '🇧🇹',
        format: '99 99 99 99',
      },
      {
        name: 'Maldives',
        dial_code: '+960',
        code: 'MV',
        flag: '🇲🇻',
        format: '999-9999',
      },
      {
        name: 'Australia',
        dial_code: '+61',
        code: 'AU',
        flag: '🇦🇺',
        format: '999 999 999',
      },
      {
        name: 'New Zealand',
        dial_code: '+64',
        code: 'NZ',
        flag: '🇳🇿',
        format: '99 999 9999',
      },
      {
        name: 'South Africa',
        dial_code: '+27',
        code: 'ZA',
        flag: '🇿🇦',
        format: '99 999 9999',
      },
      {
        name: 'Egypt',
        dial_code: '+20',
        code: 'EG',
        flag: '🇪🇬',
        format: '99 9999 9999',
      },
      {
        name: 'Morocco',
        dial_code: '+212',
        code: 'MA',
        flag: '🇲🇦',
        format: '99 99 99 99 99',
      },
      {
        name: 'Algeria',
        dial_code: '+213',
        code: 'DZ',
        flag: '🇩🇿',
        format: '99 99 99 99 99',
      },
      {
        name: 'Tunisia',
        dial_code: '+216',
        code: 'TN',
        flag: '🇹🇳',
        format: '99 999 999',
      },
      {
        name: 'Libya',
        dial_code: '+218',
        code: 'LY',
        flag: '🇱🇾',
        format: '99 999 9999',
      },
      {
        name: 'Gambia',
        dial_code: '+220',
        code: 'GM',
        flag: '🇬🇲',
        format: '999 9999',
      },
      {
        name: 'Senegal',
        dial_code: '+221',
        code: 'SN',
        flag: '🇸🇳',
        format: '99 999 99 99',
      },
      {
        name: 'Mauritania',
        dial_code: '+222',
        code: 'MR',
        flag: '🇲🇷',
        format: '99 99 99 99',
      },
      {
        name: 'Mali',
        dial_code: '+223',
        code: 'ML',
        flag: '🇲🇱',
        format: '99 99 99 99',
      },
      {
        name: 'Guinea',
        dial_code: '+224',
        code: 'GN',
        flag: '🇬🇳',
        format: '99 99 99 99',
      },
      {
        name: 'Ivory Coast',
        dial_code: '+225',
        code: 'CI',
        flag: '🇨🇮',
        format: '99 99 99 99',
      },
      {
        name: 'Burkina Faso',
        dial_code: '+226',
        code: 'BF',
        flag: '🇧🇫',
        format: '99 99 99 99',
      },
      {
        name: 'Niger',
        dial_code: '+227',
        code: 'NE',
        flag: '🇳🇪',
        format: '99 99 99 99',
      },
      {
        name: 'Togo',
        dial_code: '+228',
        code: 'TG',
        flag: '🇹🇬',
        format: '99 99 99 99',
      },
      {
        name: 'Benin',
        dial_code: '+229',
        code: 'BJ',
        flag: '🇧🇯',
        format: '99 99 99 99',
      },
      {
        name: 'Mauritius',
        dial_code: '+230',
        code: 'MU',
        flag: '🇲🇺',
        format: '999 9999',
      },
      {
        name: 'Liberia',
        dial_code: '+231',
        code: 'LR',
        flag: '🇱🇷',
        format: '99 999 9999',
      },
      {
        name: 'Sierra Leone',
        dial_code: '+232',
        code: 'SL',
        flag: '🇸🇱',
        format: '99 999999',
      },
      {
        name: 'Ghana',
        dial_code: '+233',
        code: 'GH',
        flag: '🇬🇭',
        format: '99 999 9999',
      },
      {
        name: 'Nigeria',
        dial_code: '+234',
        code: 'NG',
        flag: '🇳🇬',
        format: '999 999 9999',
      },
      {
        name: 'Chad',
        dial_code: '+235',
        code: 'TD',
        flag: '🇹🇩',
        format: '99 99 99 99',
      },
      {
        name: 'Central African Republic',
        dial_code: '+236',
        code: 'CF',
        flag: '🇨🇫',
        format: '99 99 99 99',
      },
      {
        name: 'Cameroon',
        dial_code: '+237',
        code: 'CM',
        flag: '🇨🇲',
        format: '9999 99 99',
      },
      {
        name: 'Cape Verde',
        dial_code: '+238',
        code: 'CV',
        flag: '🇨🇻',
        format: '999 99 99',
      },
      {
        name: 'São Tomé and Príncipe',
        dial_code: '+239',
        code: 'ST',
        flag: '🇸🇹',
        format: '99 99999',
      },
      {
        name: 'Equatorial Guinea',
        dial_code: '+240',
        code: 'GQ',
        flag: '🇬🇶',
        format: '999 999 999',
      },
      {
        name: 'Gabon',
        dial_code: '+241',
        code: 'GA',
        flag: '🇬🇦',
        format: '99 99 99 99',
      },
      {
        name: 'Republic of the Congo',
        dial_code: '+242',
        code: 'CG',
        flag: '🇨🇬',
        format: '99 999 9999',
      },
      {
        name: 'Democratic Republic of the Congo',
        dial_code: '+243',
        code: 'CD',
        flag: '🇨🇩',
        format: '999 999 999',
      },
      {
        name: 'Angola',
        dial_code: '+244',
        code: 'AO',
        flag: '🇦🇴',
        format: '999 999 999',
      },
      {
        name: 'Guinea-Bissau',
        dial_code: '+245',
        code: 'GW',
        flag: '🇬🇼',
        format: '999 999 999',
      },
      {
        name: 'British Indian Ocean Territory',
        dial_code: '+246',
        code: 'IO',
        flag: '🇮🇴',
        format: '999 9999',
      },
      {
        name: 'Seychelles',
        dial_code: '+248',
        code: 'SC',
        flag: '🇸🇨',
        format: '99 99 99 99',
      },
      {
        name: 'Sudan',
        dial_code: '+249',
        code: 'SD',
        flag: '🇸🇩',
        format: '99 999 9999',
      },
      {
        name: 'Rwanda',
        dial_code: '+250',
        code: 'RW',
        flag: '🇷🇼',
        format: '99 999 9999',
      },
      {
        name: 'Ethiopia',
        dial_code: '+251',
        code: 'ET',
        flag: '🇪🇹',
        format: '99 999 9999',
      },
      {
        name: 'Somalia',
        dial_code: '+252',
        code: 'SO',
        flag: '🇸🇴',
        format: '99 999 9999',
      },
      {
        name: 'Djibouti',
        dial_code: '+253',
        code: 'DJ',
        flag: '🇩🇯',
        format: '99 99 99 99',
      },
      {
        name: 'Kenya',
        dial_code: '+254',
        code: 'KE',
        flag: '🇰🇪',
        format: '999 999 999',
      },
      {
        name: 'Tanzania',
        dial_code: '+255',
        code: 'TZ',
        flag: '🇹🇿',
        format: '99 999 9999',
      },
      {
        name: 'Uganda',
        dial_code: '+256',
        code: 'UG',
        flag: '🇺🇬',
        format: '99 999 9999',
      },
      {
        name: 'Burundi',
        dial_code: '+257',
        code: 'BI',
        flag: '🇧🇮',
        format: '99 99 99 99',
      },
      {
        name: 'Mozambique',
        dial_code: '+258',
        code: 'MZ',
        flag: '🇲🇿',
        format: '99 999 9999',
      },
      {
        name: 'Zambia',
        dial_code: '+260',
        code: 'ZM',
        flag: '🇿🇲',
        format: '99 999 9999',
      },
      {
        name: 'Madagascar',
        dial_code: '+261',
        code: 'MG',
        flag: '🇲🇬',
        format: '99 99 999 99',
      },
      {
        name: 'Réunion',
        dial_code: '+262',
        code: 'RE',
        flag: '🇷🇪',
        format: '999 99 99 99',
      },
      {
        name: 'Zimbabwe',
        dial_code: '+263',
        code: 'ZW',
        flag: '🇿🇼',
        format: '99 999 9999',
      },
      {
        name: 'Namibia',
        dial_code: '+264',
        code: 'NA',
        flag: '🇳🇦',
        format: '99 999 9999',
      },
      {
        name: 'Malawi',
        dial_code: '+265',
        code: 'MW',
        flag: '🇲🇼',
        format: '99 999 9999',
      },
      {
        name: 'Lesotho',
        dial_code: '+266',
        code: 'LS',
        flag: '🇱🇸',
        format: '99 999 9999',
      },
      {
        name: 'Botswana',
        dial_code: '+267',
        code: 'BW',
        flag: '🇧🇼',
        format: '99 999 9999',
      },
      {
        name: 'Swaziland',
        dial_code: '+268',
        code: 'SZ',
        flag: '🇸🇿',
        format: '99 999 9999',
      },
      {
        name: 'Comoros',
        dial_code: '+269',
        code: 'KM',
        flag: '🇰🇲',
        format: '99 99 99 99',
      },
      {
        name: 'Saint Helena',
        dial_code: '+290',
        code: 'SH',
        flag: '🇸🇭',
        format: '9999',
      },
      {
        name: 'Eritrea',
        dial_code: '+291',
        code: 'ER',
        flag: '🇪🇷',
        format: '9 999 999',
      },
      {
        name: 'Aruba',
        dial_code: '+297',
        code: 'AW',
        flag: '🇦🇼',
        format: '999 9999',
      },
      {
        name: 'Faroe Islands',
        dial_code: '+298',
        code: 'FO',
        flag: '🇫🇴',
        format: '999999',
      },
      {
        name: 'Greenland',
        dial_code: '+299',
        code: 'GL',
        flag: '🇬🇱',
        format: '99 99 99',
      },
      {
        name: 'Gibraltar',
        dial_code: '+350',
        code: 'GI',
        flag: '🇬🇮',
        format: '999 99999',
      },
      {
        name: 'Luxembourg',
        dial_code: '+352',
        code: 'LU',
        flag: '🇱🇺',
        format: '999 999 999',
      },
      {
        name: 'Ireland',
        dial_code: '+353',
        code: 'IE',
        flag: '🇮🇪',
        format: '99 999 9999',
      },
      {
        name: 'Iceland',
        dial_code: '+354',
        code: 'IS',
        flag: '🇮🇸',
        format: '999 9999',
      },
      {
        name: 'Albania',
        dial_code: '+355',
        code: 'AL',
        flag: '🇦🇱',
        format: '99 999 9999',
      },
      {
        name: 'Malta',
        dial_code: '+356',
        code: 'MT',
        flag: '🇲🇹',
        format: '9999 9999',
      },
      {
        name: 'Cyprus',
        dial_code: '+357',
        code: 'CY',
        flag: '🇨🇾',
        format: '99 999 999',
      },
      {
        name: 'Armenia',
        dial_code: '+374',
        code: 'AM',
        flag: '🇦🇲',
        format: '99 999 999',
      },
      {
        name: 'Andorra',
        dial_code: '+376',
        code: 'AD',
        flag: '🇦🇩',
        format: '999 999',
      },
      {
        name: 'Monaco',
        dial_code: '+377',
        code: 'MC',
        flag: '🇲🇨',
        format: '99 99 99 99',
      },
      {
        name: 'San Marino',
        dial_code: '+378',
        code: 'SM',
        flag: '🇸🇲',
        format: '99 99 99 99',
      },
      {
        name: 'Serbia',
        dial_code: '+381',
        code: 'RS',
        flag: '🇷🇸',
        format: '99 999 9999',
      },
      {
        name: 'Montenegro',
        dial_code: '+382',
        code: 'ME',
        flag: '🇲🇪',
        format: '99 999 999',
      },
      {
        name: 'Kosovo',
        dial_code: '+383',
        code: 'XK',
        flag: '🇽🇰',
        format: '99 999 999',
      },
      {
        name: 'Bosnia and Herzegovina',
        dial_code: '+387',
        code: 'BA',
        flag: '🇧🇦',
        format: '99 999 999',
      },
      {
        name: 'North Macedonia',
        dial_code: '+389',
        code: 'MK',
        flag: '🇲🇰',
        format: '99 999 999',
      },
      {
        name: 'Liechtenstein',
        dial_code: '+423',
        code: 'LI',
        flag: '🇱🇮',
        format: '999 999 999',
      },
      {
        name: 'Falkland Islands',
        dial_code: '+500',
        code: 'FK',
        flag: '🇫🇰',
        format: '99999',
      },
      {
        name: 'Belize',
        dial_code: '+501',
        code: 'BZ',
        flag: '🇧🇿',
        format: '999 9999',
      },
      {
        name: 'Guatemala',
        dial_code: '+502',
        code: 'GT',
        flag: '🇬🇹',
        format: '9999 9999',
      },
      {
        name: 'El Salvador',
        dial_code: '+503',
        code: 'SV',
        flag: '🇸🇻',
        format: '9999 9999',
      },
      {
        name: 'Honduras',
        dial_code: '+504',
        code: 'HN',
        flag: '🇭🇳',
        format: '9999 9999',
      },
      {
        name: 'Nicaragua',
        dial_code: '+505',
        code: 'NI',
        flag: '🇳🇮',
        format: '9999 9999',
      },
      {
        name: 'Costa Rica',
        dial_code: '+506',
        code: 'CR',
        flag: '🇨🇷',
        format: '9999 9999',
      },
      {
        name: 'Panama',
        dial_code: '+507',
        code: 'PA',
        flag: '🇵🇦',
        format: '9999 9999',
      },
      {
        name: 'Saint Pierre and Miquelon',
        dial_code: '+508',
        code: 'PM',
        flag: '🇵🇲',
        format: '999 99 99',
      },
      {
        name: 'Haiti',
        dial_code: '+509',
        code: 'HT',
        flag: '🇭🇹',
        format: '99 99 9999',
      },
      {
        name: 'Guadeloupe',
        dial_code: '+590',
        code: 'GP',
        flag: '🇬🇵',
        format: '999 99 99 99',
      },
      {
        name: 'Bolivia',
        dial_code: '+591',
        code: 'BO',
        flag: '🇧🇴',
        format: '999 999 999',
      },
      {
        name: 'Guyana',
        dial_code: '+592',
        code: 'GY',
        flag: '🇬🇾',
        format: '999 9999',
      },
      {
        name: 'Ecuador',
        dial_code: '+593',
        code: 'EC',
        flag: '🇪🇨',
        format: '99 999 9999',
      },
      {
        name: 'French Guiana',
        dial_code: '+594',
        code: 'GF',
        flag: '🇬🇫',
        format: '999 99 99 99',
      },
      {
        name: 'Paraguay',
        dial_code: '+595',
        code: 'PY',
        flag: '🇵🇾',
        format: '999 999 999',
      },
      {
        name: 'Martinique',
        dial_code: '+596',
        code: 'MQ',
        flag: '🇲🇶',
        format: '999 99 99 99',
      },
      {
        name: 'Suriname',
        dial_code: '+597',
        code: 'SR',
        flag: '🇸🇷',
        format: '999 9999',
      },
      {
        name: 'Uruguay',
        dial_code: '+598',
        code: 'UY',
        flag: '🇺🇾',
        format: '99 999 999',
      },
      {
        name: 'Netherlands Antilles',
        dial_code: '+599',
        code: 'AN',
        flag: '🇦🇳',
        format: '999 9999',
      },
      {
        name: 'East Timor',
        dial_code: '+670',
        code: 'TL',
        flag: '🇹🇱',
        format: '999 9999',
      },
      {
        name: 'Antarctica',
        dial_code: '+672',
        code: 'AQ',
        flag: '🇦🇶',
        format: '999 999',
      },
      {
        name: 'Brunei',
        dial_code: '+673',
        code: 'BN',
        flag: '🇧🇳',
        format: '999 9999',
      },
      {
        name: 'Nauru',
        dial_code: '+674',
        code: 'NR',
        flag: '🇳🇷',
        format: '999 9999',
      },
      {
        name: 'Papua New Guinea',
        dial_code: '+675',
        code: 'PG',
        flag: '🇵🇬',
        format: '999 9999',
      },
      {
        name: 'Tonga',
        dial_code: '+676',
        code: 'TO',
        flag: '🇹🇴',
        format: '999 9999',
      },
      {
        name: 'Solomon Islands',
        dial_code: '+677',
        code: 'SB',
        flag: '🇸🇧',
        format: '999 9999',
      },
      {
        name: 'Vanuatu',
        dial_code: '+678',
        code: 'VU',
        flag: '🇻🇺',
        format: '999 9999',
      },
      {
        name: 'Fiji',
        dial_code: '+679',
        code: 'FJ',
        flag: '🇫🇯',
        format: '999 9999',
      },
      {
        name: 'Palau',
        dial_code: '+680',
        code: 'PW',
        flag: '🇵🇼',
        format: '999 9999',
      },
      {
        name: 'Wallis and Futuna',
        dial_code: '+681',
        code: 'WF',
        flag: '🇼🇫',
        format: '99 99 99',
      },
      {
        name: 'Cook Islands',
        dial_code: '+682',
        code: 'CK',
        flag: '🇨🇰',
        format: '99 999',
      },
      {
        name: 'Niue',
        dial_code: '+683',
        code: 'NU',
        flag: '🇳🇺',
        format: '999 9999',
      },
      {
        name: 'American Samoa',
        dial_code: '+684',
        code: 'AS',
        flag: '🇦🇸',
        format: '999 9999',
      },
      {
        name: 'Samoa',
        dial_code: '+685',
        code: 'WS',
        flag: '🇼🇸',
        format: '99 99999',
      },
      {
        name: 'Kiribati',
        dial_code: '+686',
        code: 'KI',
        flag: '🇰🇮',
        format: '999 9999',
      },
      {
        name: 'New Caledonia',
        dial_code: '+687',
        code: 'NC',
        flag: '🇳🇨',
        format: '99 99 99',
      },
      {
        name: 'Tuvalu',
        dial_code: '+688',
        code: 'TV',
        flag: '🇹🇻',
        format: '999 9999',
      },
      {
        name: 'French Polynesia',
        dial_code: '+689',
        code: 'PF',
        flag: '🇵🇫',
        format: '99 99 99 99',
      },
      {
        name: 'Tokelau',
        dial_code: '+690',
        code: 'TK',
        flag: '🇹🇰',
        format: '999 9999',
      },
      {
        name: 'Micronesia',
        dial_code: '+691',
        code: 'FM',
        flag: '🇫🇲',
        format: '999 9999',
      },
      {
        name: 'Marshall Islands',
        dial_code: '+692',
        code: 'MH',
        flag: '🇲🇭',
        format: '999 9999',
      },
      {
        name: 'North Korea',
        dial_code: '+850',
        code: 'KP',
        flag: '🇰🇵',
        format: '999 999 9999',
      },
      {
        name: 'Hong Kong',
        dial_code: '+852',
        code: 'HK',
        flag: '🇭🇰',
        format: '9999 9999',
      },
      {
        name: 'Macau',
        dial_code: '+853',
        code: 'MO',
        flag: '🇲🇴',
        format: '9999 9999',
      },
      {
        name: 'Cambodia',
        dial_code: '+855',
        code: 'KH',
        flag: '🇰🇭',
        format: '99 999 999',
      },
      {
        name: 'Laos',
        dial_code: '+856',
        code: 'LA',
        flag: '🇱🇦',
        format: '99 999 999',
      },
      {
        name: 'Taiwan',
        dial_code: '+886',
        code: 'TW',
        flag: '🇹🇼',
        format: '999 999 999',
      },
      {
        name: 'Lebanon',
        dial_code: '+961',
        code: 'LB',
        flag: '🇱🇧',
        format: '99 999 999',
      },
      {
        name: 'Jordan',
        dial_code: '+962',
        code: 'JO',
        flag: '🇯🇴',
        format: '99 999 9999',
      },
      {
        name: 'Syria',
        dial_code: '+963',
        code: 'SY',
        flag: '🇸🇾',
        format: '99 999 9999',
      },
      {
        name: 'Iraq',
        dial_code: '+964',
        code: 'IQ',
        flag: '🇮🇶',
        format: '99 999 9999',
      },
      {
        name: 'Kuwait',
        dial_code: '+965',
        code: 'KW',
        flag: '🇰🇼',
        format: '999 99999',
      },
      {
        name: 'Saudi Arabia',
        dial_code: '+966',
        code: 'SA',
        flag: '🇸🇦',
        format: '99 999 9999',
      },
      {
        name: 'Yemen',
        dial_code: '+967',
        code: 'YE',
        flag: '🇾🇪',
        format: '99 999 999',
      },
      {
        name: 'Oman',
        dial_code: '+968',
        code: 'OM',
        flag: '🇴🇲',
        format: '999 999 999',
      },
      {
        name: 'Palestine',
        dial_code: '+970',
        code: 'PS',
        flag: '🇵🇸',
        format: '99 999 9999',
      },
      {
        name: 'United Arab Emirates',
        dial_code: '+971',
        code: 'AE',
        flag: '🇦🇪',
        format: '99 999 9999',
      },
      {
        name: 'Israel',
        dial_code: '+972',
        code: 'IL',
        flag: '🇮🇱',
        format: '99-999-9999',
      },
      {
        name: 'Bahrain',
        dial_code: '+973',
        code: 'BH',
        flag: '🇧🇭',
        format: '999 999 999',
      },
      {
        name: 'Qatar',
        dial_code: '+974',
        code: 'QA',
        flag: '🇶🇦',
        format: '999 999 999',
      },
      {
        name: 'Mongolia',
        dial_code: '+976',
        code: 'MN',
        flag: '🇲🇳',
        format: '99 99 99 99',
      },
      {
        name: 'Tajikistan',
        dial_code: '+992',
        code: 'TJ',
        flag: '🇹🇯',
        format: '99 999 9999',
      },
      {
        name: 'Turkmenistan',
        dial_code: '+993',
        code: 'TM',
        flag: '🇹🇲',
        format: '99 99 99 99',
      },
      {
        name: 'Azerbaijan',
        dial_code: '+994',
        code: 'AZ',
        flag: '🇦🇿',
        format: '99 999 99 99',
      },
      {
        name: 'Georgia',
        dial_code: '+995',
        code: 'GE',
        flag: '🇬🇪',
        format: '999 999 999',
      },
      {
        name: 'Kyrgyzstan',
        dial_code: '+996',
        code: 'KG',
        flag: '🇰🇬',
        format: '999 999 999',
      },
      {
        name: 'Uzbekistan',
        dial_code: '+998',
        code: 'UZ',
        flag: '🇺🇿',
        format: '99 999 99 99',
      },
    ],
    selectedDialCode: '+1',
    phoneNumber: '',

    getSelectedCountry() {
      return (
        this.intCodes.find(
          (element) => element.dial_code === this.selectedDialCode,
        ) || null
      );
    },

    formatPhoneNumber(value) {
      const selectedCountry = this.getSelectedCountry();
      const formatter = new AsYouType(selectedCountry?.code || 'US');
      return formatter.input(String(value || ''));
    },

    onPhoneInputHandler(event) {
      this.phoneNumber = this.formatPhoneNumber(event.target.value);
    },

    onSelectChangeHandler() {
      // Clear the phone number when country changes to avoid formatting conflicts
      this.phoneNumber = '';
    },

    async detectUserCountry() {
      try {
        // Method 1: Try browser timezone detection
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const countryFromTimezone = this.getCountryFromTimezone(timezone);
        if (countryFromTimezone) {
          return countryFromTimezone;
        }

        // Method 2: Try browser language detection
        const language = navigator.language || navigator.languages[0];
        const countryFromLanguage = this.getCountryFromLanguage(language);
        if (countryFromLanguage) {
          return countryFromLanguage;
        }

        // Method 3: Fallback to IP geolocation (optional, requires external service)
        // Uncomment the following lines if you want to use IP geolocation
        /*
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        if (data.country_code) {
          return data.country_code;
        }
        */
      } catch (error) {
        console.log('Country detection failed:', error);
      }

      return 'US'; // Default fallback
    },

    getCountryFromTimezone(timezone) {
      // Map common timezones to country codes
      const timezoneMap = {
        'America/New_York': 'US',
        'America/Chicago': 'US',
        'America/Denver': 'US',
        'America/Los_Angeles': 'US',
        'America/Toronto': 'CA',
        'America/Vancouver': 'CA',
        'Europe/London': 'GB',
        'Europe/Paris': 'FR',
        'Europe/Berlin': 'DE',
        'Europe/Rome': 'IT',
        'Europe/Madrid': 'ES',
        'Europe/Amsterdam': 'NL',
        'Europe/Brussels': 'BE',
        'Europe/Zurich': 'CH',
        'Europe/Vienna': 'AT',
        'Europe/Copenhagen': 'DK',
        'Europe/Stockholm': 'SE',
        'Europe/Oslo': 'NO',
        'Europe/Helsinki': 'FI',
        'Europe/Lisbon': 'PT',
        'Europe/Athens': 'GR',
        'Europe/Warsaw': 'PL',
        'Europe/Prague': 'CZ',
        'Europe/Bratislava': 'SK',
        'Europe/Budapest': 'HU',
        'Europe/Bucharest': 'RO',
        'Europe/Sofia': 'BG',
        'Europe/Zagreb': 'HR',
        'Europe/Ljubljana': 'SI',
        'Europe/Tallinn': 'EE',
        'Europe/Riga': 'LV',
        'Europe/Vilnius': 'LT',
        'Europe/Moscow': 'RU',
        'Europe/Kiev': 'UA',
        'Europe/Minsk': 'BY',
        'Europe/Chisinau': 'MD',
        'Asia/Tokyo': 'JP',
        'Asia/Shanghai': 'CN',
        'Asia/Seoul': 'KR',
        'Asia/Singapore': 'SG',
        'Asia/Kuala_Lumpur': 'MY',
        'Asia/Bangkok': 'TH',
        'Asia/Ho_Chi_Minh': 'VN',
        'Asia/Manila': 'PH',
        'Asia/Jakarta': 'ID',
        'Asia/Kolkata': 'IN',
        'Asia/Karachi': 'PK',
        'Asia/Dhaka': 'BD',
        'Asia/Colombo': 'LK',
        'Asia/Kathmandu': 'NP',
        'Asia/Thimphu': 'BT',
        'Asia/Male': 'MV',
        'Australia/Sydney': 'AU',
        'Australia/Melbourne': 'AU',
        'Pacific/Auckland': 'NZ',
        'Africa/Johannesburg': 'ZA',
        'Africa/Cairo': 'EG',
        'Africa/Casablanca': 'MA',
        'Africa/Algiers': 'DZ',
        'Africa/Tunis': 'TN',
        'Africa/Tripoli': 'LY',
      };

      return timezoneMap[timezone] || null;
    },

    getCountryFromLanguage(language) {
      // Extract country code from language tag (e.g., 'en-US' -> 'US')
      const parts = language.split('-');
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }

      // Map common language codes to countries
      const languageMap = {
        en: 'US',
        fr: 'FR',
        de: 'DE',
        es: 'ES',
        it: 'IT',
        pt: 'PT',
        nl: 'NL',
        sv: 'SE',
        no: 'NO',
        da: 'DK',
        fi: 'FI',
        pl: 'PL',
        cs: 'CZ',
        sk: 'SK',
        hu: 'HU',
        ro: 'RO',
        bg: 'BG',
        hr: 'HR',
        sl: 'SI',
        et: 'EE',
        lv: 'LV',
        lt: 'LT',
        ru: 'RU',
        uk: 'UA',
        be: 'BY',
        ja: 'JP',
        zh: 'CN',
        ko: 'KR',
        th: 'TH',
        vi: 'VN',
        id: 'ID',
        hi: 'IN',
        ar: 'EG',
      };

      return languageMap[parts[0]] || null;
    },

    async init() {
      // Detect user's country and set initial values
      const detectedCountry = await this.detectUserCountry();
      const country = this.intCodes.find((c) => c.code === detectedCountry);

      if (country) {
        this.selectedDialCode = country.dial_code;
      }
    },
  };
}

// Make phoneNumberComponent available globally
window.phoneNumberComponent = phoneNumberComponent;

export default function registerStoreCartStore() {
  Alpine.store('storeCartStore', {
    calendarId: null,
    calendarSlug: null,
    editModeBooking: null,
    fullCartBooking: null,
    bookingItemsCount: 0,
    bookingUrl: '',
    sessionTimeout: null,
    SESSION_TIMEOUT: 15 * 60 * 1000, // 15 minutes for inactivity timeout
    PAYPAL_SESSION_TIMEOUT: 30 * 60 * 1000, // 30 minutes while PayPal checkout is in progress
    paypalPendingOrderId: '',
    inactivityTimer: null,
    lastActivityTime: null,
    showExpiredModal: false,
    activityInterval: null,
    availableExtras: [],
    selectedExtras: {},
    calendarExtras: [],
    selectedExtrasDropdowns: {},
    addedExtraOptions: {},
    toastStore: null, // Will be initialized after Alpine is ready

    bookingType: null,
    bookingMessage: '', // For request booking message
    showPaymentUnavailableModal: false, // For the instant booking message modal
    calendarMode: null,

    // Guest information
    guestInfo: {
      email: '',
      firstName: '',
      lastName: '',
      phone_number: '',
      phone_country_code: '+1', // Default to US
    },
    customFields: {}, // For custom checkout fields
    bookingMessage: '',

    // Store bound event handlers
    blurHandler: null,
    focusHandler: null,

    stripe: null,
    elements: null,
    paymentElement: null,
    isPaymentElementMounted: false,
    isPaymentElementReady: false,
    stripeMountObserver: null,
    stripeMountPoller: null,
    clientSecret: null,
    checkoutPaymentIntentInFlight: false,
    availablePaymentProviders: [],
    selectedPaymentProvider: 'stripe',
    defaultPaymentProvider: 'stripe',
    paypalButtons: null,
    paypalButtonsRendered: false,
    paypalUseRedirectCheckout: false,
    isPaypalCheckoutProcessing: false,

    sessionId: null,
    calendarCurrency: 'EUR',

    selectedDeposit: null,
    depositOptions: [],
    totalAmount: 0,
    trackingStepViewKey: null,
    guestInfoTrackingBookingId: null,
    guestInfoStartedTracked: false,
    guestInfoCompletedTracked: false,

    stripeAppearance: {
      theme: 'stripe',
      variables: {
        colorPrimary: '#000000',
      },
    },

    termsAccepted: false,
    showTermsModal: false,
    termsContent: '',

    // Coupon code state
    couponCode: '',
    appliedCoupon: null, // { discount_name, total_saved, coupon_code, discount_id }
    isApplyingCoupon: false,
    couponError: '',

    // Partner access code state
    partnerCode: '',
    authenticatedPartner: null, // { id, name, payment_mode, commission_rate, access_code_label, access_code_always_instant_book }
    partnerBookingCode: '',
    isPartnerLoggingIn: false,
    partnerLoginError: '',

    // Add submit message state for banner
    submitMessage: '',
    messageType: '',
    isLoading: false,

    // Helper function to extract error messages from various response formats
    extractErrorMessage(data, defaultMessage = 'An error occurred') {
      if (typeof data === 'string') {
        return data;
      }

      if (data && typeof data === 'object') {
        // Check for direct error field
        if (data.error) {
          return data.error;
        }

        // Check for validation errors
        if (data.errors) {
          if (typeof data.errors === 'object' && !Array.isArray(data.errors)) {
            const errorMessages = [];
            for (const field in data.errors) {
              if (Array.isArray(data.errors[field])) {
                errorMessages.push(...data.errors[field]);
              } else {
                errorMessages.push(data.errors[field]);
              }
            }
            return errorMessages.length > 0
              ? errorMessages.join(', ')
              : defaultMessage;
          } else if (Array.isArray(data.errors)) {
            return data.errors.join(', ');
          } else {
            return data.errors;
          }
        }

        // Check for message field
        if (data.message) {
          return data.message;
        }
      }

      return defaultMessage;
    },

    toTrackingNumber(value, fallback = 0) {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    },

    getTrackingStep() {
      const path = window.location.pathname || '';
      if (path.includes('/checkout/')) return 'checkout';
      if (path.includes('/extras/')) return 'extras';
      if (path.includes('/cart/')) return 'cart';
      return 'store';
    },

    getTrackingCurrency() {
      return this.fullCartBooking?.currency || this.calendarCurrency || 'EUR';
    },

    getTrackingItems() {
      if (!this.fullCartBooking) {
        return [];
      }

      const rawItems = Array.isArray(this.fullCartBooking.booking_items)
        ? this.fullCartBooking.booking_items
        : [];

      // Hidden package components roll their price into the primary item so a
      // package booking is reported as ONE item (the package listing) at the
      // full package price—matching what the visitor saw and added to cart.
      const secondaryPriceByParent = {};
      rawItems.forEach((item) => {
        if (item?.is_secondary_package_item && item?.parent_booking_item) {
          const parentId = String(item.parent_booking_item);
          secondaryPriceByParent[parentId] =
            (secondaryPriceByParent[parentId] || 0) +
            this.toTrackingNumber(
              item?.total_final_price ?? item?.final_price ?? item?.base_price,
              0,
            );
        }
      });

      const bookingItems = rawItems
        .filter((item) => !item?.is_secondary_package_item)
        .map((item) => {
          const itemQuantity = Math.max(
            1,
            this.toTrackingNumber(item?.item_count, 1) || 1,
          );
          const ownPrice = this.toTrackingNumber(
            item?.total_final_price ?? item?.final_price ?? item?.base_price,
            0,
          );
          const nestedPackageItems = Array.isArray(item?.nested_package_items)
            ? item.nested_package_items
            : [];
          const nestedPrice = nestedPackageItems.reduce((sum, component) => {
            const componentPrice = this.toTrackingNumber(
              component?.total_final_price ??
                component?.final_price ??
                component?.base_price ??
                component?.price,
              0,
            );
            const componentCount = this.toTrackingNumber(
              component?.count ?? component?.quantity,
              1,
            );
            return sum + componentPrice * Math.max(1, componentCount);
          }, 0);
          const packageInfo = item?.package_info;
          if (
            packageInfo?.package_id ||
            item?.package_rate_set_id ||
            item?.package_name
          ) {
            const packageLineTotal =
              ownPrice +
              (secondaryPriceByParent[String(item?.id)] || 0) +
              nestedPrice;
            return {
              item_id: String(
                packageInfo?.package_id ||
                  item?.package_rate_set_id ||
                  item?.rate_set?.id ||
                  item?.id ||
                  '',
              ),
              item_name:
                packageInfo?.package_name || item?.package_name || 'Package',
              item_category: 'package',
              quantity: itemQuantity,
              // Keep grouped rows as a single GA4 item with quantity>1 and
              // per-unit price for cleaner item metrics (units sold, etc.).
              price: packageLineTotal / itemQuantity,
            };
          }
          const listingLineTotal = ownPrice;
          return {
            item_id: String(item?.rate_set?.id || item?.id || ''),
            item_name: item?.rate_set?.public_name || 'Listing',
            item_category: item?.rate_set?.is_package
              ? 'package'
              : item?.rate_set?.accommodation_type
              ? 'accommodation'
              : item?.rate_set?.tour_type
              ? 'tour'
              : 'listing',
            quantity: itemQuantity,
            price: listingLineTotal / itemQuantity,
          };
        });

      const extras = Array.isArray(this.fullCartBooking.booking_extras)
        ? this.fullCartBooking.booking_extras
            .filter((extra) => extra?.source_type !== 'PACKAGE_COMPONENT')
            .map((extra) => ({
              item_id: `extra-${extra?.extra_id || extra?.id || ''}`,
              item_name: extra?.name || 'Extra',
              item_category: extra?.booking_item_id ? 'add_on' : 'extra',
              quantity: this.toTrackingNumber(extra?.amount, 1),
              price: this.toTrackingNumber(
                extra?.final_price ??
                  this.toTrackingNumber(extra?.unit_price, 0) *
                    this.toTrackingNumber(extra?.amount, 1),
                0,
              ),
            }))
        : [];

      return [...bookingItems, ...extras];
    },

    buildTrackingPayload(extraPayload = {}) {
      const totalAmount = this.toTrackingNumber(
        this.fullCartBooking?.total_price ??
          this.totalAmount ??
          this.getTotalWithExtras(),
        0,
      );
      const depositPercentage =
        this.toTrackingNumber(this.selectedDeposit, 100) || 100;
      const paymentDueNow = (totalAmount * depositPercentage) / 100;

      return {
        booking_id:
          this.editModeBooking?.id || this.fullCartBooking?.id || null,
        booking_reference: this.fullCartBooking?.booking_number || null,
        session_id: this.getSessionId() || null,
        step: this.getTrackingStep(),
        booking_type: this.bookingType || null,
        payment_provider: this.selectedPaymentProvider || null,
        currency: this.getTrackingCurrency(),
        value: totalAmount,
        total_amount: totalAmount,
        deposit_percentage: depositPercentage,
        deposit_amount: paymentDueNow,
        balance_due_later: Math.max(0, totalAmount - paymentDueNow),
        item_count: this.toTrackingNumber(this.bookingItemsCount, 0),
        items: this.getTrackingItems(),
        coupon_code: this.appliedCoupon?.coupon_code || null,
        ...extraPayload,
      };
    },

    normalizeTrackingPayloadForEvent(eventName, payload) {
      const normalized = { ...payload };
      const checkoutPricingEvents = new Set([
        'checkout_option_selected',
        'add_payment_info',
        'checkout_submit_attempt',
      ]);

      if (!checkoutPricingEvents.has(eventName)) {
        // Cart and extras events should not carry checkout-only payment context.
        delete normalized.deposit_percentage;
        delete normalized.deposit_amount;
        delete normalized.balance_due_later;
      }

      if (
        eventName !== 'add_payment_info' &&
        eventName !== 'checkout_submit_attempt'
      ) {
        delete normalized.payment_provider;
      }

      if (eventName !== 'checkout_submit_attempt') {
        delete normalized.booking_type;
      }

      return normalized;
    },

    trackCheckoutEvent(eventName, payload = {}, dedupeKey = null) {
      const tracker = window.HightideTracking;
      if (!tracker || typeof tracker.track !== 'function') {
        return;
      }

      const mergedPayload = this.normalizeTrackingPayloadForEvent(
        eventName,
        this.buildTrackingPayload(payload),
      );
      if (dedupeKey) {
        mergedPayload._dedupe_key = dedupeKey;
      }
      tracker.track(eventName, mergedPayload);
    },

    trackCheckoutStepView() {
      const bookingId = this.editModeBooking?.id || this.fullCartBooking?.id;
      if (!bookingId) {
        return;
      }

      const step = this.getTrackingStep();
      const stepKey = `${bookingId}:${step}`;
      if (this.trackingStepViewKey === stepKey) {
        return;
      }

      this.trackingStepViewKey = stepKey;
      if (step === 'cart') {
        this.trackCheckoutEvent('view_cart', { step }, stepKey);
      } else if (step === 'checkout') {
        this.trackCheckoutEvent(
          'begin_checkout',
          { step, source: 'checkout_page_load' },
          stepKey,
        );
      } else if (step === 'extras') {
        this.trackCheckoutEvent(
          'checkout_option_selected',
          {
            step,
            option_type: 'checkout_step',
            option_value: 'extras',
          },
          stepKey,
        );
      }
    },

    handleBookingTypeSelection(bookingType) {
      this.bookingType = bookingType;
      if (bookingType !== 'instant') {
        this.unmountStripePaymentElement();
      }
      this.trackCheckoutEvent('checkout_option_selected', {
        option_type: 'booking_type',
        option_value: bookingType,
      });
    },

    handlePaymentProviderSelection(providerCode) {
      this.selectedPaymentProvider = providerCode;
      if (providerCode !== 'stripe') {
        this.unmountStripePaymentElement();
      }
      this.trackCheckoutEvent('add_payment_info', {
        payment_provider: providerCode,
        option_type: 'payment_provider',
        option_value: providerCode,
      });
    },

    trackGuestInfoProgress() {
      const step = this.getTrackingStep();
      if (step !== 'checkout') {
        return;
      }

      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        return;
      }

      if (this.guestInfoTrackingBookingId !== bookingId) {
        this.guestInfoTrackingBookingId = bookingId;
        this.guestInfoStartedTracked = false;
        this.guestInfoCompletedTracked = false;
      }

      const firstName = String(this.guestInfo.firstName || '').trim();
      const lastName = String(this.guestInfo.lastName || '').trim();
      const email = String(this.guestInfo.email || '').trim();
      const phoneNumber = String(this.guestInfo.phone_number || '').trim();
      const hasAnyDetail = Boolean(
        firstName || lastName || email || phoneNumber,
      );
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const hasCompleteCoreDetails = Boolean(
        firstName && lastName && email && emailRegex.test(email),
      );

      if (hasAnyDetail && !this.guestInfoStartedTracked) {
        this.trackCheckoutEvent(
          'checkout_option_selected',
          {
            option_type: 'guest_details',
            option_value: 'started',
            step: 'checkout',
          },
          `guest_details_started:${bookingId}`,
        );
        this.guestInfoStartedTracked = true;
      }

      if (hasCompleteCoreDetails && !this.guestInfoCompletedTracked) {
        this.trackCheckoutEvent(
          'checkout_option_selected',
          {
            option_type: 'guest_details',
            option_value: 'completed',
            step: 'checkout',
          },
          `guest_details_completed:${bookingId}`,
        );
        this.guestInfoCompletedTracked = true;
      }
    },

    initialize(
      calendarId,
      calendarSlug,
      calendarMode,
      terms,
      calendarCurrency = 'EUR',
    ) {
      console.log('Initializing store with mode:', calendarMode);
      this.calendarMode = calendarMode;
      this.calendarCurrency = calendarCurrency || 'EUR';

      // Initialize toastStore reference
      this.toastStore = Alpine.store('toastStore');

      this.calendarId = calendarId;
      this.calendarSlug = calendarSlug;

      this.bookingType = calendarMode === 'RO' ? 'request' : 'instant';

      // Initial load with mutation observer
      const observer = new MutationObserver((mutations, obs) => {
        const paymentContainer = document.getElementById('payment-element');
        if (
          paymentContainer &&
          typeof Stripe !== 'undefined' &&
          this.selectedPaymentProvider === 'stripe'
        ) {
          this.initializeStripe();
          this.ensureCheckoutPaymentIntent();
          this.initializePaymentElement();
          obs.disconnect(); // Stop observing once we've initialized
        }
      });

      // Start observing the document for the payment element
      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      // Keep Stripe element mount state in sync with checkout UI state.
      Alpine.effect(() => {
        if (
          this.bookingType !== 'instant' ||
          calendarMode === 'RO' ||
          this.selectedPaymentProvider !== 'stripe'
        ) {
          this.unmountStripePaymentElement();
          return;
        }

        setTimeout(() => {
          const paymentContainer = document.getElementById('payment-element');
          if (paymentContainer) {
            this.initializePaymentElement();
          }
        }, 0);
      });

      // Track when guests begin/complete checkout contact details without
      // waiting for submit, so non-submitted checkout abandonment is visible.
      Alpine.effect(() => {
        this.guestInfo.firstName;
        this.guestInfo.lastName;
        this.guestInfo.email;
        this.guestInfo.phone_number;
        this.trackGuestInfoProgress();
      });

      // Clean up any existing handlers first
      this.cleanup();

      console.log('🚀 Initializing storeCartStore...', {
        calendarId,
        calendarSlug,
      });

      // Create bound event handlers
      this.blurHandler = () => {
        console.log('🔴 Window lost focus');
        if (this.editModeBooking) {
          // CRITICAL: Stop heartbeat when window loses focus
          this.stopActivityUpdates();
          console.log('⏸️ Stopped heartbeat - starting inactivity countdown');
          this.startInactivityTimer();
        }
      };

      this.focusHandler = () => {
        console.log('🟢 Window gained focus');

        // CRITICAL: Stop inactivity timer when window regains focus
        this.resetInactivityTimer();

        const sessionId = this.getSessionId();
        if (!sessionId) {
          console.log('⚠️ No session found, creating new session');
          this.createNewSession();
        }

        // Check booking status and restart heartbeat if booking is active
        this.checkBookingStatus().then(() => {
          const isCartPage =
            window.location.pathname.includes('bookings/cart/');

          if (
            isCartPage &&
            (!this.editModeBooking || !this.bookingItemsCount)
          ) {
            console.log('⚠️ Invalid cart state detected, redirecting to home');
            this.setExpiredMessage();
            if (this.calendarSlug) {
              window.location.href = `/bookings/${this.calendarSlug}/home/`;
            }
          } else if (this.editModeBooking) {
            // CRITICAL: Restart heartbeat when window regains focus with active booking
            console.log('▶️ Restarting heartbeat - window has focus');
            this.startActivityUpdates();
          }
        });
      };

      // Add event listeners
      window.addEventListener('blur', this.blurHandler);
      window.addEventListener('focus', this.focusHandler);

      // CRITICAL: Validate session consistency across storage locations
      console.log('🔍 Validating session consistency on page load...');
      const sessionSources = {
        sessionStorage: sessionStorage.getItem('bookingSessionId'),
        localStorage: localStorage.getItem('bookingSessionId'),
        cookie: this.getCookie('bookingSessionId'),
      };

      // Check if session IDs are inconsistent
      const uniqueSessions = new Set(
        Object.values(sessionSources).filter(Boolean),
      );
      if (uniqueSessions.size > 1) {
        console.warn(
          '⚠️ CRITICAL: Session ID mismatch detected!',
          sessionSources,
        );
        console.warn(
          'This could cause booking loss - syncing to most recent session',
        );
      }

      // Add timestamp to localStorage when creating a booking
      let inactiveTime = null;
      const lastActiveTime = localStorage.getItem('lastActiveTime');
      const currentTime = Date.now();

      if (lastActiveTime) {
        inactiveTime = currentTime - parseInt(lastActiveTime);
        // Check if less than 15 minutes have passed
        if (inactiveTime < this.SESSION_TIMEOUT) {
          console.log('📝 Resuming previous session');
          // Try to restore the previous session
          const sessionId = this.getSessionId();
          if (sessionId) {
            console.log('CALLING CHECK EXISTING BOOKING');
            this.checkExistingBooking(sessionId, calendarId);
          }
        } else {
          // Clear old session data if more than 15 minutes passed
          console.log('🧹 Clearing expired session');
          this.resetStore();
          this.createNewSession();
        }
      }

      // Debug current state
      console.log('Initial state:', {
        editModeBooking: this.editModeBooking,
        bookingItemsCount: this.bookingItemsCount,
        sessionId: this.getSessionId(),
      });

      // CRITICAL: Initialize session - getSessionId will sync if exists, otherwise create new
      const sessionId = this.getSessionId() || this.createNewSession();
      console.log('📝 Session ID after initialization:', sessionId);

      // Only call checkExistingBooking once
      if (!lastActiveTime || inactiveTime >= this.SESSION_TIMEOUT) {
        console.log('CALLING CHECK EXISTING BOOKING');
        this.checkExistingBooking(sessionId, calendarId).then((isValid) => {
          if (!isValid && this.editModeBooking) {
            // If we had a booking but it's now invalid/expired, clear everything
            console.log(
              '🚨 Booking became invalid during check, clearing state',
            );
            this.resetStore();
            this.createNewSession();
          }
        });
      }

      // Check for session expired message
      this.checkExpiredMessage();

      // Start activity update interval if there's an active booking
      if (this.editModeBooking) {
        this.startActivityUpdates();
      }

      // Set up cleanup when Alpine store is destroyed
      Alpine.effect(() => {
        return () => this.cleanup();
      });

      // Get terms from the calendar data
      console.log('Terms:', terms);
      const calendarTerms = terms || '';
      if (calendarTerms) {
        this.termsContent = calendarTerms;
      }
    },

    initializePaymentProviders(providerCodes = [], defaultProvider = '') {
      const normalized = Array.isArray(providerCodes)
        ? providerCodes.filter((provider) => typeof provider === 'string')
        : [];

      this.availablePaymentProviders = normalized;

      const fallbackProvider = normalized.includes('stripe')
        ? 'stripe'
        : normalized[0] || 'stripe';
      this.defaultPaymentProvider = defaultProvider || fallbackProvider;
      this.selectedPaymentProvider = normalized.includes(
        this.defaultPaymentProvider,
      )
        ? this.defaultPaymentProvider
        : fallbackProvider;
    },

    setPayPalRedirectCheckout(enabled) {
      this.paypalUseRedirectCheckout = Boolean(enabled);
    },

    clearPayPalPendingOrder() {
      this.paypalPendingOrderId = '';
      if (this.editModeBooking) {
        this.editModeBooking.paypal_order_id = '';
      }
      if (this.fullCartBooking) {
        this.fullCartBooking.paypal_order_id = '';
      }
    },

    getInactivityTimeoutMs() {
      const booking = this.editModeBooking || this.fullCartBooking;
      const paypalOrderId =
        (booking && booking.paypal_order_id) || this.paypalPendingOrderId || '';
      if (String(paypalOrderId).trim()) {
        return this.PAYPAL_SESSION_TIMEOUT;
      }
      return this.SESSION_TIMEOUT;
    },

    async startPayPalRedirectCheckout() {
      if (
        this.selectedPaymentProvider !== 'paypal' ||
        this.bookingType !== 'instant' ||
        this.calendarMode === 'RO'
      ) {
        return;
      }

      if (this.isPaypalCheckoutProcessing) {
        return;
      }

      if (!this.validateGuestInfo()) {
        return;
      }
      if (!this.termsAccepted) {
        this.setSubmitMessage(
          'Please accept the Terms and Conditions',
          'warning',
        );
        return;
      }
      if (!this.editModeBooking?.id) {
        this.setSubmitMessage('Missing booking context.', 'warning');
        return;
      }

      const currentSessionId = this.getSessionId();
      if (!currentSessionId) {
        this.setSubmitMessage('Missing checkout session.', 'warning');
        return;
      }

      this.isPaypalCheckoutProcessing = true;
      const depositPercentage = this.selectedDeposit ?? 100;

      try {
        this.trackCheckoutEvent('checkout_submit_attempt', {
          submit_mode: 'instant',
          payment_provider: 'paypal',
          result: 'redirect_to_paypal_hosted',
        });
        await this.persistGuestInfoForCheckout(
          currentSessionId,
          depositPercentage,
        );

        const paypalOrderParams = {
          session_id: currentSessionId,
          deposit_percentage: String(depositPercentage),
        };
        try {
          if (
            window.HightideTracking &&
            typeof window.HightideTracking.getAttributionSnapshotForSync ===
              'function'
          ) {
            const attributionSnapshot =
              window.HightideTracking.getAttributionSnapshotForSync();
            if (attributionSnapshot) {
              paypalOrderParams.tracking_attribution =
                JSON.stringify(attributionSnapshot);
              if (attributionSnapshot.consent_state) {
                paypalOrderParams.consent_state =
                  attributionSnapshot.consent_state;
              }
            }
          }
        } catch (attributionError) {
          console.warn(
            'Non-fatal: could not attach tracking attribution to PayPal order create',
            attributionError,
          );
        }
        const orderResponse = await fetch(
          `/paypal/store/${this.calendarSlug}/checkout/${this.editModeBooking.id}/create-order/`,
          {
            method: 'POST',
            headers: {
              'X-CSRFToken': getCSRFToken(),
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams(paypalOrderParams),
          },
        );
        const orderData = await orderResponse.json();
        if (!orderResponse.ok || !orderData.approval_url) {
          throw new Error(
            orderData.error || 'Failed to create PayPal checkout order',
          );
        }

        this.paypalPendingOrderId = orderData.order_id || '';
        if (this.editModeBooking) {
          this.editModeBooking.paypal_order_id = this.paypalPendingOrderId;
        }
        if (this.fullCartBooking) {
          this.fullCartBooking.paypal_order_id = this.paypalPendingOrderId;
        }

        window.location.href = orderData.approval_url;
      } catch (error) {
        console.error('PayPal redirect checkout error:', error);
        this.setSubmitMessage(
          error.message || 'Could not start PayPal checkout.',
          'warning',
        );
        this.isPaypalCheckoutProcessing = false;
      }
    },

    initializeStripe() {
      if (
        this.calendarMode !== 'RO' &&
        this.selectedPaymentProvider === 'stripe' &&
        this.availablePaymentProviders.includes('stripe') &&
        !this.stripe &&
        typeof Stripe !== 'undefined'
      ) {
        // Initialize Stripe with the connected account
        this.stripe = Stripe(STRIPE_PUBLISHABLE_KEY, {
          stripeAccount: STRIPE_ACCOUNT_ID,
        });

        // Optional: HTML checkout no longer embeds a PaymentIntent secret.
        if (typeof CLIENT_SECRET === 'string' && CLIENT_SECRET) {
          this.clientSecret = CLIENT_SECRET;
        }
      }
    },

    ensureCheckoutPaymentIntent(attempt = 0) {
      if (
        this.calendarMode === 'RO' ||
        this.selectedPaymentProvider !== 'stripe' ||
        this.authenticatedPartner
      ) {
        return Promise.resolve();
      }
      if (this.clientSecret || this.checkoutPaymentIntentInFlight) {
        return Promise.resolve();
      }
      const checkoutPage = document.querySelector(
        '[data-page-type="store-checkout"]',
      );
      if (!checkoutPage) {
        return Promise.resolve();
      }
      if (typeof Stripe === 'undefined') {
        if (attempt < 40) {
          setTimeout(() => this.ensureCheckoutPaymentIntent(attempt + 1), 50);
        } else {
          this.showPaymentUnavailableModal = true;
        }
        return Promise.resolve();
      }
      this.initializeStripe();
      this.checkoutPaymentIntentInFlight = true;
      const deposit = this.selectedDeposit ?? 100;
      return Promise.resolve(this.updatePaymentIntent(deposit)).finally(() => {
        this.checkoutPaymentIntentInFlight = false;
      });
    },

    initializePaymentElement(reinitialize = false) {
      if (this.selectedPaymentProvider !== 'stripe') {
        return;
      }

      if (!this.stripe) {
        this.initializeStripe();
      }

      // If reinitializing or first time setup
      if (
        (reinitialize || !this.elements) &&
        this.stripe &&
        this.clientSecret
      ) {
        // Clear existing elements if reinitializing
        if (reinitialize && this.paymentElement) {
          this.paymentElement.destroy();
          this.paymentElement = null;
          this.elements = null;
          this.isPaymentElementMounted = false;
          this.isPaymentElementReady = false;
        }

        // Initialize Elements
        this.elements = this.stripe.elements({
          clientSecret: this.clientSecret,
          appearance: this.stripeAppearance,
        });

        // Create new Payment Element
        this.paymentElement = this.elements.create('payment', {
          layout: 'tabs',
          defaultValues: {
            billingDetails: {
              name: `${this.guestInfo.firstName || ''} ${
                this.guestInfo.lastName || ''
              }`,
              email: this.guestInfo.email || '',
            },
          },
          wallets: {
            applePay: 'auto',
            googlePay: 'auto',
          },
        });
        this.isPaymentElementReady = false;
        if (typeof this.paymentElement.on === 'function') {
          this.paymentElement.on('ready', () => {
            this.isPaymentElementReady = true;
          });
        }
      }

      this.mountStripePaymentElementWhenVisible();
    },

    isElementVisibleInViewport(element) {
      if (!element || typeof element.getBoundingClientRect !== 'function') {
        return false;
      }
      const rect = element.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      // Mount Stripe only when the section is meaningfully reached, not when
      // it barely intersects the viewport.
      const mountTriggerY = viewportHeight * 0.82;
      return rect.top <= mountTriggerY && rect.bottom > 0;
    },

    disconnectStripeMountObserver() {
      if (this.stripeMountObserver) {
        this.stripeMountObserver.disconnect();
        this.stripeMountObserver = null;
      }
      if (this.stripeMountPoller) {
        clearInterval(this.stripeMountPoller);
        this.stripeMountPoller = null;
      }
    },

    mountStripePaymentElementNow() {
      const paymentContainer = document.getElementById('payment-element');
      if (!paymentContainer) {
        console.error('Payment element container not found');
        return;
      }
      if (!this.paymentElement || this.isPaymentElementMounted) {
        return;
      }

      this.isPaymentElementReady = false;
      mountStripePaymentElement(this.paymentElement, '#payment-element');
      this.isPaymentElementMounted = true;
      this.disconnectStripeMountObserver();
      console.log('Payment element mounted');
    },

    async ensureStripePaymentElementReadyForSubmit() {
      if (this.selectedPaymentProvider !== 'stripe') {
        return true;
      }

      if (!this.paymentElement) {
        return false;
      }

      if (!this.isPaymentElementMounted) {
        this.mountStripePaymentElementNow();
      }

      if (this.isPaymentElementReady) {
        return true;
      }

      const readyTimeoutMs = 4000;
      const pollMs = 50;
      const startedAt = Date.now();

      while (Date.now() - startedAt < readyTimeoutMs) {
        if (this.isPaymentElementReady) {
          return true;
        }
        await new Promise((resolve) => setTimeout(resolve, pollMs));
      }

      // Fall back to mounted state so Stripe can still return a precise error
      // message if readiness is delayed by the browser.
      return this.isPaymentElementMounted;
    },

    mountStripePaymentElementWhenVisible() {
      if (!this.paymentElement || this.isPaymentElementMounted) {
        return;
      }

      const paymentContainer = document.getElementById('payment-element');
      if (!paymentContainer) {
        console.error('Payment element container not found');
        return;
      }

      if (this.isElementVisibleInViewport(paymentContainer)) {
        this.mountStripePaymentElementNow();
        return;
      }

      this.disconnectStripeMountObserver();
      this.stripeMountObserver = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((entry) => {
            const viewportHeight =
              window.innerHeight || document.documentElement.clientHeight;
            const mountTriggerY = viewportHeight * 0.82;
            return (
              entry.boundingClientRect.top <= mountTriggerY &&
              entry.boundingClientRect.bottom > 0
            );
          });
          if (isVisible) {
            this.mountStripePaymentElementNow();
          }
        },
        {
          root: null,
          // Use permissive thresholds; position check above decides actual mount.
          rootMargin: '0px',
          threshold: [0, 0.01],
        },
      );
      this.stripeMountObserver.observe(paymentContainer);

      // Fallback for browsers/layouts where IntersectionObserver doesn't fire
      // reliably for this container until after user interaction.
      this.stripeMountPoller = setInterval(() => {
        if (this.isPaymentElementMounted) {
          this.disconnectStripeMountObserver();
          return;
        }
        if (this.isElementVisibleInViewport(paymentContainer)) {
          this.mountStripePaymentElementNow();
        }
      }, 250);
    },

    unmountStripePaymentElement() {
      this.disconnectStripeMountObserver();
      if (
        this.paymentElement &&
        typeof this.paymentElement.unmount === 'function'
      ) {
        try {
          this.paymentElement.unmount();
        } catch (error) {
          console.warn(
            'Unable to unmount Stripe payment element cleanly:',
            error,
          );
        }
      }
      this.isPaymentElementMounted = false;
      this.isPaymentElementReady = false;
    },

    async persistGuestInfoForCheckout(currentSessionId, depositPercentage) {
      const storeGuestInfoResponse = await fetch(
        '/bookings_app/store-guest-info/',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking.id,
            guest_info: {
              ...this.guestInfo,
              phone_number:
                this.guestInfo.phone_country_code + this.guestInfo.phone_number,
            },
            custom_fields: this.customFields,
            booking_message: this.bookingMessage || '',
            session_id: currentSessionId,
            calendar_id: this.calendarId,
            deposit_percentage: depositPercentage,
          }),
        },
      );

      if (!storeGuestInfoResponse.ok) {
        const errorData = await storeGuestInfoResponse.json();
        const errorMessage = this.extractErrorMessage(
          errorData,
          'Failed to store guest information',
        );
        throw new Error(errorMessage);
      }
    },

    initializePayPalButtons(force = false) {
      // PayPal checkout is redirect-only in BYO credentials mode.
      return;
    },

    // Add cleanup method
    cleanup() {
      console.log('🧹 Cleaning up storeCartStore...');
      this.unmountStripePaymentElement();

      // Remove event listeners if they exist
      if (this.blurHandler) {
        window.removeEventListener('blur', this.blurHandler);
        this.blurHandler = null;
      }
      if (this.focusHandler) {
        window.removeEventListener('focus', this.focusHandler);
        this.focusHandler = null;
      }

      // Clear all intervals and timers
      if (this.inactivityTimer) {
        clearInterval(this.inactivityTimer);
        this.inactivityTimer = null;
      }
      if (this.activityInterval) {
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }

      this.paypalButtons = null;
      this.paypalButtonsRendered = false;
      this.isPaypalCheckoutProcessing = false;
      this.paypalPendingOrderId = '';
    },

    resetInactivityTimer() {
      console.log('Attempting to reset inactivity timer');
      if (this.editModeBooking) {
        if (this.inactivityTimer) {
          console.log('🔄 Clearing existing timer');
          clearInterval(this.inactivityTimer);
          this.inactivityTimer = null;
        }
        this.lastActivityTime = Date.now();
        this.updateActivityTimestamp();
      } else {
        console.log('No active booking, skip timer reset');
      }
    },

    startInactivityTimer() {
      console.log('Attempting to start inactivity timer', {
        editModeBooking: this.editModeBooking,
        existingTimer: this.inactivityTimer,
      });

      if (!this.editModeBooking) {
        console.log('⏭️ No active booking, skipping timer');
        return;
      }

      // Clear any existing timer
      if (this.inactivityTimer) {
        clearInterval(this.inactivityTimer);
      }

      console.log('▶️ Starting new countdown timer');
      this.lastActivityTime = Date.now();

      // Reduced frequency of checks to every 5 seconds and added buffer time
      this.inactivityTimer = setInterval(() => {
        const inactiveTime = Date.now() - this.lastActivityTime;
        const sessionTimeout = this.getInactivityTimeoutMs();
        const timeRemaining = sessionTimeout - inactiveTime;

        // Add 5-second buffer to prevent premature timeouts
        if (timeRemaining <= -5000) {
          console.log('⚠️ Session timeout reached!');
          clearInterval(this.inactivityTimer);
          this.handleSessionTimeout();
        }
      }, 5000); // Check every 5 seconds instead of 1
    },

    async handleSessionTimeout() {
      // Prevent multiple timeout handlers
      if (this._handlingTimeout) {
        console.log('Session timeout already being handled, skipping...');
        return;
      }
      this._handlingTimeout = true;

      console.log('🛑 Handling session timeout');

      try {
        // Stop the timer first
        if (this.inactivityTimer) {
          clearInterval(this.inactivityTimer);
          this.inactivityTimer = null;
        }

        const sessionId = this.getSessionId();
        if (sessionId && this.calendarId && this.editModeBooking?.id) {
          const response = await fetch('/bookings/expire-session/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              session_id: sessionId,
              calendar_id: this.calendarId,
              booking_id: this.editModeBooking.id,
            }),
          });

          const payload = await response.json().catch(() => ({}));

          if (response.ok && payload.success) {
            console.log('✅ Successfully expired booking on server');
            this.fullCartBooking = null;
            this.editModeBooking = null;
            this.bookingItemsCount = 0;
            this.paypalPendingOrderId = '';

            this.setExpiredMessage();

            if (this.calendarSlug) {
              window.location.href = `/bookings/${this.calendarSlug}/home/`;
            }
          } else if (response.ok && payload.deferred) {
            console.log(
              '⏳ Server deferred session expiry (PayPal checkout still within hold window)',
            );
            this.startInactivityTimer();
          }
        }
      } catch (error) {
        console.error('❌ Error expiring booking:', error);
      } finally {
        // Always clean up the timeout flag
        this._handlingTimeout = false;
        this.resetStore();
        this.createNewSession();
      }
    },

    async checkBookingStatus() {
      console.log('🔄 === checkBookingStatus START ===');
      console.log('🔄 Session sources:', this.getAllSessionSources());

      // Log current state
      console.log('🔄 Current state:', {
        sessionId: this.getSessionId(),
        currentBooking: this.editModeBooking,
        calendarId: this.calendarId,
      });

      const sessionId = this.getSessionId();
      const currentBooking = this.editModeBooking;

      console.log('Current state:', {
        sessionId,
        currentBooking,
        calendarId: this.calendarId,
      });

      if (!sessionId || !currentBooking) {
        console.log('No session or booking to check');
        console.log('=== checkBookingStatus END ===\n');
        return;
      }

      try {
        console.log('Making request with:', {
          session_id: sessionId,
          calendar_id: this.calendarId,
        });

        const response = await fetch('/bookings/check-temp-booking/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            session_id: sessionId,
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();
        console.log('Server response:', data);

        if (!response.ok) {
          throw new Error('Failed to check booking status');
        }

        if (data.success && data.booking) {
          console.log('Found active booking:', data.booking);
          this.editModeBooking = data.booking;

          // Calculate count after grouping (if booking has items)
          if (
            data.booking.booking_items &&
            data.booking.booking_items.length > 0
          ) {
            const groupedItems = this.groupPrimaryBookingItems(
              data.booking.booking_items,
              data.booking.booking_extras,
            );
            this.bookingItemsCount = groupedItems.length;
            console.log(
              '🔢 Calculated booking items count after grouping:',
              this.bookingItemsCount,
            );
          } else {
            this.bookingItemsCount = 0;
          }

          this.updateBookingCartUrl();

          this.lastActivityTime = Date.now();
          localStorage.setItem(
            'lastActiveTime',
            this.lastActivityTime.toString(),
          );

          console.log('Updated store state:', {
            editModeBooking: this.editModeBooking,
            bookingItemsCount: this.bookingItemsCount,
            lastActivityTime: this.lastActivityTime,
          });
        } else {
          console.log('No active booking found');
          this.resetStore();
        }
      } catch (error) {
        console.error('Error checking booking status:', error);
        this.resetStore();
      }
      console.log('=== checkBookingStatus END ===\n');
    },

    resetStore() {
      this.unmountStripePaymentElement();

      // CRITICAL: Preserve calendarId and calendarSlug - they're environment vars, not booking state
      const preservedCalendarId = this.calendarId;
      const preservedCalendarSlug = this.calendarSlug;
      const preservedCalendarMode = this.calendarMode;

      // Reset all store properties
      this.editModeBooking = null;
      this.guestInfo = {
        email: '',
        firstName: '',
        lastName: '',
        phone_number: '',
        phone_country_code: '+1', // Default to US
      };
      this.customFields = {};
      this.bookingMessage = '';
      this.bookingType = preservedCalendarMode === 'RO' ? 'request' : 'instant';
      this.guestInfoTrackingBookingId = null;
      this.guestInfoStartedTracked = false;
      this.guestInfoCompletedTracked = false;
      this.isLoading = false;

      // CRITICAL: Restore calendar info (these should never be cleared)
      this.calendarId = preservedCalendarId;
      this.calendarSlug = preservedCalendarSlug;

      // Use createNewSession to properly set up a new session
      const newSessionId = this.createNewSession();

      console.log('Store reset complete with new session:', newSessionId);
      console.log('Preserved calendar info:', {
        calendarId: this.calendarId,
        calendarSlug: this.calendarSlug,
      });
    },

    getSessionId() {
      // CRITICAL: Check ALL storage methods to prevent session loss
      const sources = {
        sessionStorage: sessionStorage.getItem('bookingSessionId'),
        localStorage: localStorage.getItem('bookingSessionId'),
        cookie: this.getCookie('bookingSessionId'),
      };

      console.log('📝 Session sources:', sources);

      // Prioritize sessionStorage, then localStorage, then cookie
      const sessionId =
        sources.sessionStorage || sources.localStorage || sources.cookie;

      if (sessionId) {
        // CRITICAL: Sync session to ALL storage locations to prevent loss
        try {
          if (sources.sessionStorage !== sessionId) {
            console.log('⚠️ Syncing session to sessionStorage');
            sessionStorage.setItem('bookingSessionId', sessionId);
          }
          if (sources.localStorage !== sessionId) {
            console.log('⚠️ Syncing session to localStorage');
            localStorage.setItem('bookingSessionId', sessionId);
          }
          if (sources.cookie !== sessionId) {
            console.log('⚠️ Syncing session to cookie');
            this.setCookie('bookingSessionId', sessionId, 1);
          }
          this.sessionId = sessionId; // Update store's sessionId
          console.log('✅ Session retrieved and synced:', sessionId);
        } catch (e) {
          console.error('❌ Error syncing session:', e);
        }
      } else {
        console.log('ℹ️ No session found in any storage location');
      }

      return sessionId;
    },

    createNewSession() {
      // CRITICAL: Only create if NO session exists anywhere
      const existingSession = this.getSessionId();
      if (existingSession) {
        console.log(
          '⚠️ Session already exists, not creating new one:',
          existingSession,
        );
        return existingSession;
      }

      const tempId = 'temporary_' + Math.random().toString(36).substr(2, 9);
      console.log('🆕 Creating new session:', tempId);

      try {
        // Set in all storage locations
        sessionStorage.setItem('bookingSessionId', tempId);
        localStorage.setItem('bookingSessionId', tempId);
        this.setCookie('bookingSessionId', tempId, 1); // 1 day
        this.sessionId = tempId; // Update store's sessionId

        // Update last active time
        localStorage.setItem('lastActiveTime', Date.now().toString());

        console.log('✅ Session created successfully:', tempId);
      } catch (e) {
        console.error('❌ Storage error:', e);
      }

      return tempId;
    },

    setCookie(name, value, days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
    },

    getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    },

    async checkExistingBooking(sessionId, calendarId) {
      console.log('Checking booking...', { sessionId, calendarId });
      try {
        const response = await fetch('/bookings/check-temp-booking/', {
          method: 'POST',
          body: JSON.stringify({
            session_id: sessionId,
            calendar_id: calendarId,
          }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
        });

        const data = await response.json();
        console.log('Booking check response:', data);

        if (data.success && data.booking) {
          // Check if booking is expired
          if (data.booking.expired || data.booking.status === 'EXPIRED') {
            console.log('🚨 Expired booking detected, redirecting to homepage');
            handleExpiredSession();
            return false;
          }

          this.editModeBooking = data.booking;

          // Calculate count after grouping (if booking has items)
          if (
            data.booking.booking_items &&
            data.booking.booking_items.length > 0
          ) {
            const groupedItems = this.groupPrimaryBookingItems(
              data.booking.booking_items,
              data.booking.booking_extras,
            );
            this.bookingItemsCount = groupedItems.length;
            console.log(
              '🔢 Calculated booking items count after grouping:',
              this.bookingItemsCount,
            );
          } else {
            this.bookingItemsCount = 0;
          }

          this.updateBookingCartUrl();

          // FIXED: Start activity updates when existing booking is found
          if (!this.activityInterval) {
            console.log('🎯 Starting activity updates for existing booking');
            this.startActivityUpdates();
          }

          return true;
        } else {
          console.log('No active booking found');
          return false;
        }
      } catch (error) {
        console.error('Error checking booking:', error);
        return false;
      }
    },

    updateBookingCartUrl() {
      const bookingId = this.editModeBooking?.id;
      const calendarSlug =
        this.calendarSlug || this.editModeBooking?.calendar_slug;

      console.log('Updating booking URL with:', {
        bookingId,
        calendarSlug,
        editModeBooking: this.editModeBooking,
      });

      if (bookingId && calendarSlug) {
        this.bookingUrl = `/bookings/${calendarSlug}/cart/${bookingId}`;
        console.log('Updated booking URL:', this.bookingUrl);
      } else {
        console.warn('Missing data for URL update:', {
          bookingId,
          calendarSlug,
          editModeBooking: this.editModeBooking,
        });
      }
    },

    fetchBookingDetails(bookingId) {
      const csrfToken = getCSRFToken();
      const sessionId = this.getSessionId(); // Get current session ID

      fetch(`/bookings/cart/${bookingId}/details/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
          'X-Session-ID': sessionId, // Add session ID to headers
        },
      })
        .then((response) => response.json())
        .then((data) => {
          console.log('data.bookings:', data.booking);
          console.log('Session ID:', this.sessionId);
          console.log('Session ID2:', sessionId);

          // Check if booking is expired
          if (
            data.booking &&
            (data.booking.expired || data.booking.status === 'EXPIRED')
          ) {
            console.log(
              '🚨 Expired booking detected in fetchBookingDetails, redirecting to homepage',
            );
            handleExpiredSession();
            return;
          }

          if (data.success && data.booking && data.booking.is_temp) {
            // Only process if booking is temporary
            console.log('data.bookings:', data.booking);

            // Format the dates for each booking item
            data.booking.booking_items.forEach((item) => {
              item.start_date = this.formatDate(item.start_date);
              item.end_date = this.formatDate(item.end_date);

              // Format dates for nested package components (without year) and group duplicates
              if (
                item.nested_package_items &&
                item.nested_package_items.length > 0
              ) {
                item.nested_package_items.forEach((component) => {
                  component.start_date = this.formatDateWithoutYear(
                    component.start_date,
                  );
                  component.end_date = this.formatDateWithoutYear(
                    component.end_date,
                  );
                });
                // Group identical components
                item.nested_package_items = this.groupPackageComponents(
                  item.nested_package_items,
                  item.amount_people,
                );
              }
            });

            // Store original items for price calculations that need secondary items
            const originalItems = data.booking.booking_items;

            // Group identical primary booking items for display
            const groupedItems = this.groupPrimaryBookingItems(
              data.booking.booking_items,
            );

            // Replace booking_items with grouped version for display
            data.booking.booking_items = groupedItems;

            // Store original items separately for calculations that need ALL items
            data.booking.all_booking_items = originalItems;

            this.fullCartBooking = data.booking;
            // Ensure booking_extras is always an array
            if (!this.fullCartBooking.booking_extras) {
              this.fullCartBooking.booking_extras = [];
            }

            // Rehydrate guest details from saved checkout info (e.g. after
            // returning from PayPal) without overwriting actively typed values.
            this.hydrateGuestInfoFromBooking(this.fullCartBooking);

            // Sync selectedExtras with existing booking extras
            if (this.calendarExtras && this.calendarExtras.length > 0) {
              this.syncSelectedExtrasWithBooking();
            }

            // Keep coupon state in sync with actual booking data
            this._syncAppliedCouponFromBooking();

            console.log('fullCartBooking:', this.fullCartBooking);
            // Count grouped items (after grouping) to match what's actually displayed
            const newCount = data.booking.booking_items.length;
            console.log(
              '🔢 Setting bookingItemsCount to',
              newCount,
              'from grouped items array',
            );
            this.bookingItemsCount = newCount;
            this.trackCheckoutStepView();
            setTimeout(() => {
              this.ensureCheckoutPaymentIntent();
            }, 0);

            // Force update the count display
            setTimeout(() => {
              console.log(
                '🔢 Verifying bookingItemsCount after timeout:',
                this.bookingItemsCount,
              );
            }, 100);
          } else {
            // Stale back-navigation case: booking already finalized or no longer valid
            // for cart editing. Clear local state and return user to store home.
            const currentPath = window.location.pathname;
            const isCartPage = currentPath.includes('/cart/');
            console.log(
              '🚨 Cart booking is no longer editable, redirecting to store home',
              {
                success: data?.success,
                hasBooking: !!data?.booking,
                isTemp: data?.booking?.is_temp,
                status: data?.booking?.status,
                currentPath,
                isCartPage,
              },
            );
            this.resetStore();
            this.fullCartBooking = null;
            this.bookingItemsCount = 0;
            this.selectedExtras = {};
            // Only force redirect on the cart page. On extras/checkout flows this can
            // incorrectly bounce mobile users to home while navigation is in progress.
            if (this.calendarSlug && isCartPage) {
              window.location.href = `/bookings/${this.calendarSlug}/home/`;
            }
          }
        })
        .catch((error) => {
          console.error('Error fetching booking details:', error);
          // Fail safe: avoid infinite loading on broken/stale cart URL.
          this.resetStore();
          if (this.calendarSlug) {
            window.location.href = `/bookings/${this.calendarSlug}/home/`;
          }
        });
    },

    hydrateGuestInfoFromBooking(booking) {
      if (!booking || !booking.guest_info) {
        if (booking && !String(this.bookingMessage || '').trim()) {
          this.bookingMessage = String(booking.booking_message || '').trim();
        }
        return;
      }

      const hasLocalGuestInfo = Boolean(
        String(this.guestInfo.firstName || '').trim() ||
          String(this.guestInfo.lastName || '').trim() ||
          String(this.guestInfo.email || '').trim() ||
          String(this.guestInfo.phone_number || '').trim(),
      );
      if (hasLocalGuestInfo) {
        return;
      }

      const savedGuestInfo = booking.guest_info || {};
      this.guestInfo.firstName = String(savedGuestInfo.first_name || '').trim();
      this.guestInfo.lastName = String(savedGuestInfo.last_name || '').trim();
      this.guestInfo.email = String(savedGuestInfo.email || '').trim();

      const savedPhone = String(savedGuestInfo.phone_number || '').trim();
      if (!savedPhone) {
        this.guestInfo.phone_number = '';
        if (!String(this.bookingMessage || '').trim()) {
          this.bookingMessage = String(booking.booking_message || '').trim();
        }
        return;
      }

      const parsedPhone = parsePhoneNumberFromString(savedPhone);
      if (parsedPhone?.isValid()) {
        this.guestInfo.phone_country_code = `+${parsedPhone.countryCallingCode}`;
        this.guestInfo.phone_number = parsedPhone.nationalNumber || '';
        if (!String(this.bookingMessage || '').trim()) {
          this.bookingMessage = String(booking.booking_message || '').trim();
        }
        return;
      }

      this.guestInfo.phone_number = savedPhone;
      if (!String(this.bookingMessage || '').trim()) {
        this.bookingMessage = String(booking.booking_message || '').trim();
      }
    },

    /**
     * Refetch cart/booking data and update fullCartBooking and totalAmount.
     * Used after applying/removing coupon (and partner login/logout) so the total and discounts stay in sync.
     */
    async fetchCartData(booking) {
      if (!booking || !booking.id) return;
      const bookingId = booking.id;
      const csrfToken = getCSRFToken();
      const sessionId = this.getSessionId();
      try {
        const response = await fetch(`/bookings/cart/${bookingId}/details/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken,
            'X-Session-ID': sessionId,
          },
        });
        const data = await response.json();

        if (
          data.booking &&
          (data.booking.expired || data.booking.status === 'EXPIRED')
        ) {
          if (typeof handleExpiredSession === 'function') {
            handleExpiredSession();
          }
          return;
        }

        if (data.success && data.booking && data.booking.is_temp) {
          const bookingData = data.booking;

          bookingData.booking_items.forEach((item) => {
            item.start_date = this.formatDate(item.start_date);
            item.end_date = this.formatDate(item.end_date);
            if (
              item.nested_package_items &&
              item.nested_package_items.length > 0
            ) {
              item.nested_package_items.forEach((component) => {
                component.start_date = this.formatDateWithoutYear(
                  component.start_date,
                );
                component.end_date = this.formatDateWithoutYear(
                  component.end_date,
                );
              });
              item.nested_package_items = this.groupPackageComponents(
                item.nested_package_items,
                item.amount_people,
              );
            }
          });

          const originalItems = bookingData.booking_items;
          const groupedItems = this.groupPrimaryBookingItems(
            bookingData.booking_items,
            bookingData.booking_extras,
          );
          bookingData.booking_items = groupedItems;
          bookingData.all_booking_items = originalItems;

          this.fullCartBooking = bookingData;
          if (!this.fullCartBooking.booking_extras) {
            this.fullCartBooking.booking_extras = [];
          }

          if (this.calendarExtras && this.calendarExtras.length > 0) {
            this.syncSelectedExtrasWithBooking();
          }

          // Sync coupon state from refreshed booking data
          this._syncAppliedCouponFromBooking();

          this.bookingItemsCount = bookingData.booking_items.length;
          // Runtime pricing is gross-only for checkout calculations/display.
          this.totalAmount = parseFloat(bookingData.total_price) || 0;
          this.trackCheckoutStepView();
        }
      } catch (error) {
        console.error('Error in fetchCartData:', error);
      }
    },

    /**
     * Sync appliedCoupon state from the booking data returned by the API.
     * Uses has_coupon_applied flag + applied_discounts with coupon_code.
     */
    _syncAppliedCouponFromBooking() {
      if (!this.fullCartBooking) {
        this.appliedCoupon = null;
        return;
      }

      // If the booking flag says no coupon, clear state
      if (!this.fullCartBooking.has_coupon_applied) {
        this.appliedCoupon = null;
        return;
      }

      // Flag is true, find the coupon discount details from applied_discounts
      const couponDiscount = (
        this.fullCartBooking.applied_discounts || []
      ).find((d) => d.coupon_code);

      if (couponDiscount) {
        this.appliedCoupon = {
          discount_name: couponDiscount.name,
          total_saved: couponDiscount.amount_saved,
          coupon_code: couponDiscount.coupon_code,
          discount_id: couponDiscount.discount_id,
        };
      } else {
        // Flag is true but no detail found — still block re-application
        this.appliedCoupon = {
          discount_name: 'Coupon',
          total_saved: '0',
          coupon_code: '',
          discount_id: null,
        };
      }
    },

    removeBookingItem(itemIdOrGroup) {
      // Handle both single ID and grouped IDs array
      const itemIds = Array.isArray(itemIdOrGroup)
        ? itemIdOrGroup
        : [itemIdOrGroup];
      const displayId = itemIds[0]; // Use first ID for UI element reference

      const itemElement = document.querySelector(
        `[data-item-id="${displayId}"]`,
      );
      if (!itemElement || itemElement.dataset.isDeleting === 'true') return;

      // Mark as deleting
      itemElement.dataset.isDeleting = 'true';

      // First step: fade out
      itemElement.style.opacity = '0';
      itemElement.style.transition = 'opacity 0.5s ease-out';

      // Wait for fade out to complete
      setTimeout(() => {
        // Second step: collapse
        const height = itemElement.offsetHeight;
        itemElement.style.height = `${height}px`;
        itemElement.style.overflow = 'hidden';
        itemElement.style.transition =
          'height 0.5s ease-out, margin 0.5s ease-out, padding 0.5s ease-out';

        // Trigger reflow
        itemElement.offsetHeight;

        // Collapse the item
        itemElement.style.height = '0';
        itemElement.style.marginTop = '0';
        itemElement.style.marginBottom = '0';
        itemElement.style.paddingTop = '0';
        itemElement.style.paddingBottom = '0';
      }, 500); // Wait for fade out to complete

      const csrfToken = getCSRFToken();

      // Use bulk endpoint for efficiency - single request instead of N requests
      const removeItemsBulk = async () => {
        try {
          const sessionId = this.getSessionId();
          const response = await fetch('/bookings/cart/remove-items-bulk/', {
            method: 'POST',
            body: JSON.stringify({
              item_ids: itemIds,
              session_id: sessionId,
              calendar_id: this.calendarId,
            }),
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken,
            },
          });
          return response.json();
        } catch (error) {
          console.error('Error removing items in bulk:', error);
          return null;
        }
      };

      // Execute bulk removal
      removeItemsBulk()
        .then((lastSuccessfulResponse) => {
          if (lastSuccessfulResponse && lastSuccessfulResponse.success) {
            // Wait for both fade-out and collapse animations to complete
            setTimeout(() => {
              // Remove the element from the DOM
              if (itemElement) {
                itemElement.remove();
              }

              // Format the dates for each booking item
              lastSuccessfulResponse.booking.booking_items.forEach((item) => {
                item.start_date = this.formatDate(item.start_date);
                item.end_date = this.formatDate(item.end_date);

                // Format dates for nested package components (without year) and group duplicates
                if (
                  item.nested_package_items &&
                  item.nested_package_items.length > 0
                ) {
                  item.nested_package_items.forEach((component) => {
                    component.start_date = this.formatDateWithoutYear(
                      component.start_date,
                    );
                    component.end_date = this.formatDateWithoutYear(
                      component.end_date,
                    );
                  });
                  // Group identical components
                  item.nested_package_items = this.groupPackageComponents(
                    item.nested_package_items,
                    item.amount_people,
                  );
                }
              });

              // Store original items for price calculations that need secondary items
              const originalItems =
                lastSuccessfulResponse.booking.booking_items;

              // Group identical primary booking items for display
              const groupedItems = this.groupPrimaryBookingItems(
                lastSuccessfulResponse.booking.booking_items,
                lastSuccessfulResponse.booking.booking_extras,
              );

              // Replace booking_items with grouped version for display
              lastSuccessfulResponse.booking.booking_items = groupedItems;

              // Store original items separately for calculations that need ALL items
              lastSuccessfulResponse.booking.all_booking_items = originalItems;

              this.fullCartBooking = lastSuccessfulResponse.booking;
              // Ensure booking_extras is always an array
              if (!this.fullCartBooking.booking_extras) {
                this.fullCartBooking.booking_extras = [];
              }
              console.log('fullCartBooking:', this.fullCartBooking);
              // Count grouped items (after grouping) to match what's actually displayed
              this.bookingItemsCount =
                lastSuccessfulResponse.booking.booking_items.length;

              this.trackCheckoutEvent('remove_from_cart', {
                removed_item_ids: itemIds,
                removed_item_count: itemIds.length,
              });
            }, 1000); // Wait for both fade-out and collapse animations
          }
        })
        .catch((error) => {
          console.error('Error removing booking items:', error);
        });
    },

    removeBookingExtra(extraId) {
      const csrfToken = getCSRFToken();
      const bookingId = this.getCurrentBookingId();
      fetch('/bookings/cart/remove-extra/', {
        method: 'POST',
        body: JSON.stringify({ extra_id: extraId }),
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrfToken,
        },
      })
        .then((response) => response.json())
        .then((data) => {
          if (data.success) {
            console.log('Extra removed successfully:', data.booking);

            // Clear transient extras selection state to avoid re-adding removed
            // extras from stale in-memory data on a later save.
            this.selectedExtras = {};
            this.selectedExtrasDropdowns = {};
            this.addedExtraOptions = {};

            // Always refetch canonical booking data after deletion; this keeps
            // extras, grouped items, and package presentation consistent.
            if (bookingId) {
              this.fetchBookingDetails(bookingId);
            } else if (data.booking) {
              this.fullCartBooking = data.booking;
              if (!this.fullCartBooking.booking_extras) {
                this.fullCartBooking.booking_extras = [];
              }
            }

            this.trackCheckoutEvent('remove_from_cart', {
              removed_extra_id: extraId,
              removed_item_count: 1,
            });
          } else {
            console.warn(
              'Remove extra request returned unsuccessful response:',
              data,
            );
          }
        })
        .catch((error) => {
          console.error('Error removing booking extra:', error);
        });
    },

    handleCartClick(event) {
      console.log('Cart clicked');
      event.preventDefault();

      if (!this.bookingUrl) {
        console.log('No booking URL available');
        return;
      }

      // Verify session before navigation
      const sessionId = this.getSessionId();
      if (sessionId) {
        this.checkExistingBooking(sessionId, this.calendarId).then((valid) => {
          if (valid) {
            console.log('Navigating to:', this.bookingUrl);
            window.location.href = this.bookingUrl;
          } else {
            console.log('Invalid booking state');
            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast(
                'Cart unavailable. Please try again.',
                'warning',
              );
            }
          }
        });
      }
    },

    animateCart() {
      // Select the cart element
      const cartElement = document.querySelector('.shopping-cart');
      if (cartElement) {
        // Add the animation class
        cartElement.classList.add('cart-animate');

        // Remove the animation class after animation ends
        setTimeout(() => {
          cartElement.classList.remove('cart-animate');
        }, 400); // Duration should match the animation duration in CSS
      }
    },

    formatDate(dateString) {
      const date = parseDate(dateString);
      if (!date) return dateString || '';
      const day = date.getDate().toString().padStart(2, '0'); // Ensure two-digit day
      const monthAbbreviations = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthAbbreviations[date.getMonth()]; // Get abbreviated month
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    },

    formatDateWithoutYear(dateString) {
      const date = parseDate(dateString);
      if (!date) return dateString || '';
      const day = date.getDate().toString().padStart(2, '0'); // Ensure two-digit day
      const monthAbbreviations = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
      ];
      const month = monthAbbreviations[date.getMonth()]; // Get abbreviated month
      return `${day} ${month}`;
    },

    resolvePackageComponentPeople(component) {
      const toCount = (value) => {
        const parsed = Number(value || 0);
        return Number.isFinite(parsed) ? parsed : 0;
      };

      let adults = toCount(component.total_adults || component.amount_adults);
      let children = toCount(
        component.total_children || component.amount_children,
      );
      let people = toCount(component.total_people || component.amount_people);

      if (component.person_type === 'ADULT' && adults === 0) {
        adults = people || toCount(component.count) || 1;
      } else if (component.person_type === 'CHILD' && children === 0) {
        children = people || toCount(component.count) || 1;
      }

      if (people === 0) {
        people = adults + children;
      }

      return { adults, children, people };
    },

    groupPackageComponents(components, parentPeopleCount = null) {
      /**
       * Group identical package components by:
       * - rate_set (component)
       * - start_date and end_date
       * - person_type (ADULT, CHILD, or none)
       *
       * Returns an array of grouped components with summed people counts,
       * sorted by order field
       */
      if (!components || components.length === 0) return [];

      const groupMap = new Map();

      components.forEach((component) => {
        // Create a unique key for grouping
        const key = JSON.stringify({
          rate_set_id: component.rate_set?.id,
          component_type: component.component_type || 'rate_set',
          extra_id: component.extra_id || null,
          start_date: component.start_date,
          end_date: component.end_date,
          person_type: component.person_type || null,
        });
        const peopleTotals = this.resolvePackageComponentPeople(component);

        if (!groupMap.has(key)) {
          // First occurrence - create the group
          // For person-type components, count should match the amount of that person type
          let initialCount = Number(component.count || 0);
          if (!Number.isFinite(initialCount) || initialCount <= 0) {
            initialCount = 1;
            if (component.person_type === 'ADULT' && peopleTotals.adults) {
              initialCount = peopleTotals.adults;
            } else if (
              component.person_type === 'CHILD' &&
              peopleTotals.children
            ) {
              initialCount = peopleTotals.children;
            } else if (peopleTotals.people) {
              initialCount = peopleTotals.people;
            }
          }

          groupMap.set(key, {
            ...component,
            count: initialCount,
            total_adults: peopleTotals.adults,
            total_children: peopleTotals.children,
            total_people: peopleTotals.people,
            amount_adults: peopleTotals.adults,
            amount_children: peopleTotals.children,
            amount_people: peopleTotals.people,
            order: component.order || 0, // Preserve order field
          });
        } else {
          // Add to existing group
          const existing = groupMap.get(key);
          const incomingCount = Number(component.count || 0);
          if (Number.isFinite(incomingCount) && incomingCount > 0) {
            existing.count += incomingCount;
          }
          // For person-type components, increment count by the amount of that person type
          else if (component.person_type === 'ADULT' && peopleTotals.adults) {
            existing.count += peopleTotals.adults;
          } else if (
            component.person_type === 'CHILD' &&
            peopleTotals.children
          ) {
            existing.count += peopleTotals.children;
          } else if (peopleTotals.people) {
            existing.count += peopleTotals.people;
          } else {
            existing.count += 1;
          }
          // For extras, people counts are the same on every row (same booking); do not sum
          if (component.component_type !== 'extra') {
            existing.total_adults += peopleTotals.adults;
            existing.total_children += peopleTotals.children;
            existing.total_people += peopleTotals.people;
            existing.amount_adults = existing.total_adults;
            existing.amount_children = existing.total_children;
            existing.amount_people = existing.total_people;
          }

          // Merge custom_fields_per_person arrays
          if (
            component.custom_fields_per_person &&
            component.custom_fields_per_person.length > 0
          ) {
            if (!existing.custom_fields_per_person) {
              existing.custom_fields_per_person = [];
            }
            existing.custom_fields_per_person.push(
              ...component.custom_fields_per_person,
            );
          }

          // Merge custom_fields objects (for per-item fields)
          if (
            component.custom_fields &&
            Object.keys(component.custom_fields).length > 0
          ) {
            if (!existing.custom_fields) {
              existing.custom_fields = {};
            }
            Object.assign(existing.custom_fields, component.custom_fields);
          }
        }
      });

      // Convert to array and apply a cart display fallback:
      // when a PERSON component collapses to a single row with count=1 (no adult/child split),
      // use parent people count so PER_PERSON components display correctly.
      const groupedComponents = Array.from(groupMap.values()).map(
        (component) => {
          const hasAdultChildSplit =
            (component.total_adults || 0) > 0 ||
            (component.total_children || 0) > 0;
          const fallbackPeople = Number(parentPeopleCount || 0);
          const needsPersonFallback =
            (component.person_type === 'PERSON' ||
              component.component_type === 'extra') &&
            !hasAdultChildSplit &&
            fallbackPeople > 1 &&
            Number(component.total_people || 0) <= 1;
          if (needsPersonFallback) {
            component.total_people = fallbackPeople;
            component.amount_people = fallbackPeople;
            // Keep extras quantity independent from party-size display.
            if (
              component.component_type !== 'extra' &&
              Number(component.count || 0) <= 1
            ) {
              component.count = fallbackPeople;
            }
          }
          return component;
        },
      );

      // Sort:
      // 1) non-extra components in chronological order
      // 2) extra components after all non-extras
      return groupedComponents.sort((a, b) => {
        const aIsExtra = a.component_type === 'extra' ? 1 : 0;
        const bIsExtra = b.component_type === 'extra' ? 1 : 0;
        if (aIsExtra !== bIsExtra) return aIsExtra - bIsExtra;

        // Non-extras: chronological first
        if (!aIsExtra) {
          const aStart = parseDate(a.start_date);
          const bStart = parseDate(b.start_date);
          const byDate =
            (aStart ? aStart.getTime() : 0) - (bStart ? bStart.getTime() : 0);
          if (byDate !== 0) return byDate;
          return (a.order || 0) - (b.order || 0);
        }

        // Extras: stable order by configured order then name
        const byOrder = (a.order || 9999) - (b.order || 9999);
        if (byOrder !== 0) return byOrder;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });
    },

    groupPrimaryBookingItems(items, bookingExtras = []) {
      /**
       * Group identical primary booking items (including packages) by:
       * - rate_set
       * - start_date and end_date
       * - person_type (ADULT, CHILD, PERSON, or none for multi-person items)
       *
       * Returns an array of grouped items with summed people counts and item IDs array
       */
      if (!items || items.length === 0) return [];

      // Filter out secondary package items - they're handled separately via nested_package_items
      const primaryItems = items.filter(
        (item) => !item.is_secondary_package_item,
      );

      // Separate package items from regular items for different grouping logic
      // Use is_primary flag to identify package items (not nested_package_items length)
      // because FIXED secondary components may only be linked to the first primary item
      const packageItems = primaryItems.filter(
        (item) => item.is_primary === true,
      );
      const regularItems = primaryItems.filter(
        (item) => item.is_primary !== true,
      );

      const regularGroupMap = new Map();
      const packageGroupMap = new Map();

      const normalizedExtras = Array.isArray(bookingExtras)
        ? bookingExtras
        : [];
      const getAddonSignatureForItem = (bookingItemId) => {
        const itemId = String(bookingItemId || '');
        if (!itemId) return '';
        const relevantExtras = normalizedExtras
          .filter(
            (extra) =>
              String(extra?.booking_item_id || '') === itemId &&
              extra?.source_type !== 'PACKAGE_COMPONENT',
          )
          .map((extra) =>
            JSON.stringify({
              extra_id: extra?.extra_id || extra?.id || '',
              name: extra?.name || '',
              dropdown_selection: extra?.dropdown_selection || '',
              amount: Number(extra?.amount || 0),
              final_price: Number(extra?.final_price || 0),
            }),
          )
          .sort();
        return relevantExtras.join('|');
      };

      // Group regular (non-package) items
      regularItems.forEach((item) => {
        const key = JSON.stringify({
          rate_set_id: item.rate_set?.id,
          start_date: item.start_date,
          end_date: item.end_date,
          addon_signature: getAddonSignatureForItem(item.id),
          person_type: item.person_type || null,
          has_person_split:
            (item.amount_adults > 0 && item.amount_children === 0) ||
            (item.amount_adults === 0 && item.amount_children > 0) ||
            (item.amount_adults === 0 &&
              item.amount_children === 0 &&
              item.person_type),
        });

        if (!regularGroupMap.has(key)) {
          regularGroupMap.set(key, {
            ...item,
            item_count: 1,
            grouped_item_ids: [item.id],
            total_final_price: parseFloat(item.final_price || 0),
          });
        } else {
          const existing = regularGroupMap.get(key);
          existing.item_count += 1;
          existing.grouped_item_ids.push(item.id);
          existing.total_final_price += parseFloat(item.final_price || 0);
          existing.amount_adults =
            (existing.amount_adults || 0) + (item.amount_adults || 0);
          existing.amount_children =
            (existing.amount_children || 0) + (item.amount_children || 0);
          existing.amount_people =
            (existing.amount_people || 0) + (item.amount_people || 0);

          // Aggregate custom_fields_per_person from all grouped items
          if (
            item.custom_fields_per_person &&
            item.custom_fields_per_person.length > 0
          ) {
            if (!existing.custom_fields_per_person) {
              existing.custom_fields_per_person = [];
            }
            existing.custom_fields_per_person.push(
              ...item.custom_fields_per_person,
            );
          }
        }
      });

      // Group package items
      packageItems.forEach((item) => {
        // For package items, don't include person_type in the key
        // This allows grouping of 1 Adult + 1 Child into a single container
        // Use package_rate_set_id to ensure different packages with same components are treated separately
        const key = JSON.stringify({
          package_rate_set_id: item.package_rate_set_id || item.rate_set?.id,
          start_date: item.start_date,
          end_date: item.end_date,
          addon_signature: getAddonSignatureForItem(item.id),
          is_package: true, // Ensure packages only group with other packages
        });

        if (!packageGroupMap.has(key)) {
          // Initialize group with first item
          // Convert person_type to explicit counts for proper grouping
          let initialAdults = item.amount_adults || 0;
          let initialChildren = item.amount_children || 0;
          let initialPeople = item.amount_people || 0;

          if (item.person_type === 'ADULT' && initialAdults === 0) {
            initialAdults = initialPeople || 1;
          } else if (item.person_type === 'CHILD' && initialChildren === 0) {
            initialChildren = initialPeople || 1;
          } else if (item.person_type === 'PERSON' && initialPeople === 0) {
            initialPeople = 1;
          }

          // Initialize nested_package_items with person_type converted to explicit counts
          let initialNestedItems = [];
          if (
            item.nested_package_items &&
            item.nested_package_items.length > 0
          ) {
            initialNestedItems = item.nested_package_items.map((comp) => {
              const newComp = { ...comp };
              let compAdults = comp.total_adults || comp.amount_adults || 0;
              let compChildren =
                comp.total_children || comp.amount_children || 0;
              let compPeople = comp.total_people || comp.amount_people || 0;

              if (comp.person_type === 'ADULT' && compAdults === 0) {
                compAdults = compPeople || comp.count || 1;
              } else if (comp.person_type === 'CHILD' && compChildren === 0) {
                compChildren = compPeople || comp.count || 1;
              }

              newComp.total_adults = compAdults;
              newComp.total_children = compChildren;
              newComp.total_people = compPeople;

              // Clear person_type if we have both adults and children
              if (compAdults > 0 && compChildren > 0) {
                newComp.person_type = null;
              }

              return newComp;
            });
          }

          packageGroupMap.set(key, {
            ...item,
            item_count: 1,
            grouped_item_ids: [item.id],
            total_final_price: parseFloat(item.final_price || 0),
            amount_adults: initialAdults,
            amount_children: initialChildren,
            amount_people: initialPeople,
            person_type: null, // Clear person_type for grouped items to show explicit counts
            nested_package_items: initialNestedItems,
          });
        } else {
          const existing = packageGroupMap.get(key);
          existing.item_count += 1;
          existing.grouped_item_ids.push(item.id);
          existing.total_final_price += parseFloat(item.final_price || 0);

          // Convert person_type to explicit counts before summing
          let itemAdults = item.amount_adults || 0;
          let itemChildren = item.amount_children || 0;
          let itemPeople = item.amount_people || 0;

          if (item.person_type === 'ADULT' && itemAdults === 0) {
            itemAdults = itemPeople || 1;
          } else if (item.person_type === 'CHILD' && itemChildren === 0) {
            itemChildren = itemPeople || 1;
          } else if (item.person_type === 'PERSON' && itemPeople === 0) {
            itemPeople = 1;
          }

          existing.amount_adults = (existing.amount_adults || 0) + itemAdults;
          existing.amount_children =
            (existing.amount_children || 0) + itemChildren;
          existing.amount_people = (existing.amount_people || 0) + itemPeople;

          // Aggregate custom_fields_per_person from all grouped items
          if (
            item.custom_fields_per_person &&
            item.custom_fields_per_person.length > 0
          ) {
            if (!existing.custom_fields_per_person) {
              existing.custom_fields_per_person = [];
            }
            existing.custom_fields_per_person.push(
              ...item.custom_fields_per_person,
            );
          }

          // Merge nested_package_items by grouping identical secondary components
          if (
            item.nested_package_items &&
            item.nested_package_items.length > 0
          ) {
            // If existing has no nested items yet (was initialized empty), populate from this item
            if (
              !existing.nested_package_items ||
              existing.nested_package_items.length === 0
            ) {
              // Convert person_type to explicit counts when initializing
              existing.nested_package_items = item.nested_package_items.map(
                (comp) => {
                  const newComp = { ...comp };
                  let compAdults = comp.total_adults || comp.amount_adults || 0;
                  let compChildren =
                    comp.total_children || comp.amount_children || 0;
                  let compPeople = comp.total_people || comp.amount_people || 0;

                  if (comp.person_type === 'ADULT' && compAdults === 0) {
                    compAdults = compPeople || comp.count || 1;
                  } else if (
                    comp.person_type === 'CHILD' &&
                    compChildren === 0
                  ) {
                    compChildren = compPeople || comp.count || 1;
                  }

                  newComp.total_adults = compAdults;
                  newComp.total_children = compChildren;
                  newComp.total_people = compPeople;

                  // Clear person_type if we have both adults and children
                  if (compAdults > 0 && compChildren > 0) {
                    newComp.person_type = null;
                  }

                  return newComp;
                },
              );
            } else {
              // Merge with existing nested items
              item.nested_package_items.forEach((newComponent) => {
                // Find matching component in existing nested items
                // For package components, don't match by person_type to allow grouping across person types
                const matchingComponent = existing.nested_package_items.find(
                  (existingComponent) =>
                    existingComponent.rate_set?.id ===
                      newComponent.rate_set?.id &&
                    existingComponent.start_date === newComponent.start_date &&
                    existingComponent.end_date === newComponent.end_date &&
                    existingComponent.is_primary === newComponent.is_primary,
                );

                if (matchingComponent) {
                  // Convert person_type to explicit counts before merging
                  let newAdults =
                    newComponent.total_adults ||
                    newComponent.amount_adults ||
                    0;
                  let newChildren =
                    newComponent.total_children ||
                    newComponent.amount_children ||
                    0;
                  let newPeople =
                    newComponent.total_people ||
                    newComponent.amount_people ||
                    0;

                  if (newComponent.person_type === 'ADULT' && newAdults === 0) {
                    newAdults = newPeople || newComponent.count || 1;
                  } else if (
                    newComponent.person_type === 'CHILD' &&
                    newChildren === 0
                  ) {
                    newChildren = newPeople || newComponent.count || 1;
                  }

                  // Merge counts for matching component
                  matchingComponent.count =
                    (matchingComponent.count || 0) + (newComponent.count || 0);
                  matchingComponent.total_adults =
                    (matchingComponent.total_adults || 0) + newAdults;
                  matchingComponent.total_children =
                    (matchingComponent.total_children || 0) + newChildren;
                  matchingComponent.total_people =
                    (matchingComponent.total_people || 0) + newPeople;

                  // Clear person_type if we have both adults and children after merge
                  if (
                    matchingComponent.total_adults > 0 &&
                    matchingComponent.total_children > 0
                  ) {
                    matchingComponent.person_type = null;
                  }

                  // Merge custom_fields_per_person arrays
                  if (
                    newComponent.custom_fields_per_person &&
                    newComponent.custom_fields_per_person.length > 0
                  ) {
                    if (!matchingComponent.custom_fields_per_person) {
                      matchingComponent.custom_fields_per_person = [];
                    }
                    matchingComponent.custom_fields_per_person.push(
                      ...newComponent.custom_fields_per_person,
                    );
                  }

                  // Merge custom_fields objects (for per-item fields)
                  if (
                    newComponent.custom_fields &&
                    Object.keys(newComponent.custom_fields).length > 0
                  ) {
                    if (!matchingComponent.custom_fields) {
                      matchingComponent.custom_fields = {};
                    }
                    Object.assign(
                      matchingComponent.custom_fields,
                      newComponent.custom_fields,
                    );
                  }
                } else {
                  // No match found, add as new component
                  existing.nested_package_items.push({ ...newComponent });
                }
              });
            }
          } else if (
            existing.nested_package_items &&
            existing.nested_package_items.length > 0
          ) {
            // Item has no nested items (empty because it's a secondary primary unit with no children),
            // but existing group has nested items. We need to add this item's contribution to the primary component.
            // Find the primary component in existing nested items (is_primary: true)
            const primaryComponent = existing.nested_package_items.find(
              (comp) => comp.is_primary === true,
            );

            if (
              primaryComponent &&
              item.rate_set?.id === primaryComponent.rate_set?.id
            ) {
              // Convert person_type to explicit counts before summing
              let itemAdults = item.amount_adults || 0;
              let itemChildren = item.amount_children || 0;
              let itemPeople = item.amount_people || 0;

              if (item.person_type === 'ADULT' && itemAdults === 0) {
                itemAdults = itemPeople || 1;
              } else if (item.person_type === 'CHILD' && itemChildren === 0) {
                itemChildren = itemPeople || 1;
              } else if (item.person_type === 'PERSON' && itemPeople === 0) {
                itemPeople = 1;
              }

              // This item uses the same rate_set as the primary component, merge the counts
              if (primaryComponent.person_type === 'ADULT') {
                primaryComponent.count =
                  (primaryComponent.count || 0) + (itemAdults || 0);
              } else if (primaryComponent.person_type === 'CHILD') {
                primaryComponent.count =
                  (primaryComponent.count || 0) + (itemChildren || 0);
              } else if (primaryComponent.person_type === 'PERSON') {
                primaryComponent.count =
                  (primaryComponent.count || 0) + (itemPeople || 1);
              } else if (item.person_type) {
                primaryComponent.count = (primaryComponent.count || 0) + 1;
              }
              // Sum the people counts
              primaryComponent.total_adults =
                (primaryComponent.total_adults || 0) + itemAdults;
              primaryComponent.total_children =
                (primaryComponent.total_children || 0) + itemChildren;
              primaryComponent.total_people =
                (primaryComponent.total_people || 0) + itemPeople;

              // Clear person_type if we have both adults and children after merge
              if (
                primaryComponent.total_adults > 0 &&
                primaryComponent.total_children > 0
              ) {
                primaryComponent.person_type = null;
              }
            }
          }
        }
      });

      // Convert to arrays and combine, then sort by start_date
      const groupedRegular = Array.from(regularGroupMap.values());
      const groupedPackages = Array.from(packageGroupMap.values());

      // Ensure nested package includes always render as:
      // primary/secondary components first (chronological), then extras.
      groupedPackages.forEach((pkg) => {
        if (pkg.nested_package_items && pkg.nested_package_items.length > 0) {
          // Keep primary include people/count aligned with grouped package totals.
          const primaryNested = pkg.nested_package_items.find(
            (comp) => comp.is_primary === true,
          );
          if (primaryNested) {
            const pkgPeople = Number(pkg.amount_people || 0);
            const primaryAdults = Number(primaryNested.total_adults || 0);
            const primaryChildren = Number(primaryNested.total_children || 0);
            const hasAdultChildSplit = primaryAdults > 0 || primaryChildren > 0;
            if (pkgPeople > 1 && !hasAdultChildSplit) {
              primaryNested.total_people = Math.max(
                Number(primaryNested.total_people || 0),
                pkgPeople,
              );
              primaryNested.amount_people = Math.max(
                Number(primaryNested.amount_people || 0),
                pkgPeople,
              );
              if (primaryNested.person_type === 'PERSON') {
                primaryNested.count = Math.max(
                  Number(primaryNested.count || 0),
                  pkgPeople,
                );
              }
            }
          }

          pkg.nested_package_items = this.groupPackageComponents(
            pkg.nested_package_items,
            pkg.amount_people,
          );
        }
      });

      return [...groupedRegular, ...groupedPackages].sort((a, b) => {
        const aStart = parseDate(a.start_date);
        const bStart = parseDate(b.start_date);
        return (
          (aStart ? aStart.getTime() : 0) - (bStart ? bStart.getTime() : 0)
        );
      });
    },

    formatCurrency(amount, currency) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(parseFloat(amount));
    },
    getRateSetPersonLabel(rateSet, count, lowercase = false) {
      const singular = (rateSet?.person_label_singular || 'Person').trim();
      const plural = (rateSet?.person_label_plural || 'People').trim();
      const label = Number(count) === 1 ? singular : plural;
      return lowercase ? label.toLowerCase() : label;
    },
    getPersonTypeLabel(personType, count, rateSet) {
      if (personType === 'ADULT')
        return Number(count) === 1 ? 'Adult' : 'Adults';
      if (personType === 'CHILD')
        return Number(count) === 1 ? 'Child' : 'Children';
      return this.getRateSetPersonLabel(rateSet, count);
    },
    /** True when the person icon (fa-person) should be shown; false when a non-default person label override is in use (e.g. Cat/Cats). */
    showPersonIcon(rateSet) {
      if (!rateSet) return true;
      const raw = rateSet.person_label_singular;
      if (raw == null || raw === '') return true;
      const s = String(raw).trim();
      return !s || s.toLowerCase() === 'person';
    },

    calculateNights(startDate, endDate) {
      if (!startDate || !endDate) return '';

      // Use timezone-safe utility for night calculation
      const nights = calculateNights(startDate, endDate);

      if (nights === 1) {
        return '1 Night';
      } else {
        return `${nights} Nights`;
      }
    },

    calculateDays(startDate, endDate) {
      if (!startDate || !endDate) return '';

      // Use timezone-safe utility for day calculation
      const days = calculateDaysInclusive(startDate, endDate);

      if (days === 1) {
        return '1 Day';
      } else {
        return `${days} Days`;
      }
    },

    startSessionTimer() {
      this.clearTimeouts();
      // Set session timeout
      this.sessionTimeout = setTimeout(() => {
        this.handleSessionTimeout();
      }, this.SESSION_DURATION);
    },

    clearTimeouts() {
      if (this.sessionTimeout) clearTimeout(this.sessionTimeout);
    },

    pauseSessionTimer() {
      this.clearTimeouts();
    },

    resumeSessionTimer() {
      // Only resume if there's an active booking
      if (this.editModeBooking) {
        this.startSessionTimer();
      }
    },

    async extendSession() {
      const sessionId = this.getSessionId();
      try {
        const response = await fetch('/bookings/extend-session/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            session_id: sessionId,
            calendar_id: this.calendarId,
          }),
        });

        if (response.ok) {
          this.startSessionTimer();
          if (this.toastStore && this.toastStore.addToast) {
            this.toastStore.addToast(
              'Session extended successfully',
              'success',
            );
          }
        }
      } catch (error) {
        console.error('Failed to extend session:', error);
      }
    },

    // Modify addToCart to start activity monitoring when first item is added
    async addToCart(itemData) {
      console.log('🛒 Adding item to cart:', itemData);

      // Ensure valid session exists
      const sessionId = this.getSessionId() || this.createNewSession();

      try {
        const response = await fetch('/bookings/add-to-cart/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            ...itemData,
            session_id: sessionId,
          }),
        });

        console.log('Add to cart response:', response);

        if (response.ok) {
          const data = await response.json();
          console.log('Add to cart data:', data);

          // Update store state
          this.editModeBooking = data.booking;
          this.bookingItemsCount = data.booking_items_count;
          this.updateBookingCartUrl();

          // ADDED: Ensure session is properly associated with new booking
          this.lastActivityTime = Date.now();
          localStorage.setItem(
            'lastActiveTime',
            this.lastActivityTime.toString(),
          );

          // ADDED: Reset and restart activity monitoring
          this.resetInactivityTimer();
          if (document.hidden) {
            this.startInactivityTimer();
          }

          // FIXED: Start activity updates when booking is created/updated
          if (!this.activityInterval) {
            console.log('🎯 Starting activity updates for new booking');
            this.startActivityUpdates();
          }

          console.log('📦 Cart updated:', {
            editModeBooking: this.editModeBooking,
            bookingItemsCount: this.bookingItemsCount,
            sessionId: sessionId,
            lastActivityTime: this.lastActivityTime,
          });

          this.trackCheckoutEvent('add_to_cart', {
            item_id:
              itemData?.rate_set_id ||
              itemData?.rateSetId ||
              itemData?.id ||
              null,
            source: 'store_listing',
          });

          return true;
        }
        return false;
      } catch (error) {
        console.error('Error adding to cart:', error);
        return false;
      }
    },

    // Add new method to update activity timestamp
    updateActivityTimestamp() {
      if (this.editModeBooking) {
        localStorage.setItem('lastActiveTime', Date.now().toString());
      }
    },
    closeExpiredModal() {
      this.showExpiredModal = false;
      // Reset the store state when modal is closed
      this.resetStore();
      this.createNewSession();

      // No need to redirect - user can continue browsing the store
    },

    // Add new method to periodically update activity
    startActivityUpdates() {
      // CRITICAL: Clear any existing interval to prevent duplicates
      if (this.activityInterval) {
        console.log('🔄 Clearing existing activity interval');
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }

      // Send heartbeat every 2 minutes while page is active (well before 20min timeout)
      this.activityInterval = setInterval(
        async () => {
          const sessionId = this.getSessionId();
          if (this.editModeBooking?.id && sessionId && this.calendarId) {
            console.log('💓 Sending heartbeat to keep booking alive');
            try {
              // Use extend-session endpoint which validates session connection
              const response = await fetch('/bookings/extend-session/', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'X-CSRFToken': getCSRFToken(),
                },
                body: JSON.stringify({
                  session_id: sessionId,
                  calendar_id: this.calendarId,
                }),
              });

              const data = await response.json();

              if (!response.ok || !data.success) {
                console.error('❌ Heartbeat failed - booking may have expired');
                // Check booking status to verify
                await this.checkBookingStatus();
              } else {
                console.log('✅ Heartbeat successful - booking extended');
              }
            } catch (error) {
              console.error('❌ Failed to send heartbeat:', error);
            }
          } else {
            console.log('⏭️ Skipping heartbeat - no active booking or session');
          }
        },
        2 * 60 * 1000, // 2 minutes (well before 20min backend timeout)
      );
      console.log('▶️ Activity interval started (2min heartbeat)');
    },

    stopActivityUpdates() {
      if (this.activityInterval) {
        console.log('⏹️ Stopping activity interval');
        clearInterval(this.activityInterval);
        this.activityInterval = null;
      }
    },

    // Save extras to backend (used for auto-save and navigation)
    getAllExtrasForSaving() {
      const allExtras = [];

      // First, preserve only customer-managed extras.
      // Package-defined extras are system-managed and should not be re-saved as add-ons/extras.
      if (this.fullCartBooking && this.fullCartBooking.booking_extras) {
        this.fullCartBooking.booking_extras.forEach((bookingExtra) => {
          if (bookingExtra.source_type === 'PACKAGE_COMPONENT') return;
          allExtras.push({
            id: bookingExtra.extra_id,
            amount: bookingExtra.amount,
            unit_price: bookingExtra.unit_price,
            dropdown_selection: bookingExtra.dropdown_selection || '',
            booking_item_id: bookingExtra.booking_item_id || null, // Preserve the source association
          });
        });
      }

      // Then, add NEW selections from the extras page (shown in "Additional Extras")
      const selectedExtras = this.getSelectedExtras();
      allExtras.push(...selectedExtras);

      console.log('🔄 Combined extras for saving:', {
        existingBookingExtras:
          this.fullCartBooking?.booking_extras?.length || 0,
        newSelections: selectedExtras.length,
        totalForSaving: allExtras.length,
        allExtras: allExtras,
      });

      return allExtras;
    },

    getCurrentBookingId() {
      if (this.editModeBooking?.id) {
        return this.editModeBooking.id;
      }

      // Mobile browsers can occasionally lose in-memory Alpine state.
      // Use the booking id from URL as a reliable fallback.
      const pathMatch = window.location.pathname.match(
        /^\/bookings\/[^/]+\/(?:cart|extras|checkout)\/([^/]+)\/?$/,
      );
      return pathMatch ? pathMatch[1] : null;
    },

    trackExtrasAdded(newExtras) {
      if (!Array.isArray(newExtras) || newExtras.length === 0) {
        return;
      }

      const bookingId = this.getCurrentBookingId();
      newExtras.forEach((extra) => {
        const quantity = this.toTrackingNumber(extra.amount, 1);
        const unitPrice = this.toTrackingNumber(extra.unit_price, 0);
        const itemName = extra.dropdown_selection
          ? `${extra.name} (${extra.dropdown_selection})`
          : extra.name;

        this.trackCheckoutEvent(
          'add_extra',
          {
            source: 'extras_page',
            items: [
              {
                item_id: `extra-${extra.id}`,
                item_name: itemName,
                item_category: 'extra',
                quantity: quantity,
                price: unitPrice,
              },
            ],
            value: unitPrice * quantity,
          },
          `add_extra:${bookingId}:${extra.id}:${
            extra.dropdown_selection || ''
          }:${quantity}`,
        );
      });
    },

    async saveExtras(options = {}) {
      const { refreshAfterSave = true } = options;
      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        console.log('⏭️ Cannot save extras - missing booking');
        return false;
      }

      const allExtras = this.getAllExtrasForSaving();
      const newExtras = this.getSelectedExtras();

      try {
        console.log('💾 Saving extras to backend:', allExtras);
        console.log(
          '💾 Current selectedExtras being saved:',
          this.selectedExtras,
        );
        const response = await fetch('/bookings/add-extras/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: bookingId,
            extras: allExtras,
          }),
        });

        if (response.ok) {
          console.log('✅ Extras saved successfully');
          this.trackExtrasAdded(newExtras);
          this.trackCheckoutEvent('checkout_option_selected', {
            option_type: 'extras_saved',
            option_value: allExtras.length,
          });
          // Refresh only when staying on the same page.
          // For "continue to checkout", skip this to avoid redirect races on mobile.
          if (refreshAfterSave) {
            this.fetchBookingDetails(bookingId);
          }
          return true;
        } else {
          console.error('❌ Failed to save extras:', response.status);
          return false;
        }
      } catch (error) {
        console.error('❌ Error saving extras:', error);
        return false;
      }
    },

    // Defensive check in addExtrasAndProceed
    async addExtrasAndProceed() {
      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        handleExpiredSession(`/bookings/${this.calendarSlug}/home/`);
        return;
      }

      // Keep session warm when storage was partially dropped on mobile.
      if (!this.getSessionId()) {
        this.createNewSession();
      }

      const saved = await this.saveExtras({ refreshAfterSave: false });
      if (saved) {
        this.trackCheckoutEvent('checkout_option_selected', {
          option_type: 'navigation',
          option_value: 'extras_to_checkout',
        });
        window.location.href = `/bookings/${this.calendarSlug}/checkout/${bookingId}`;
      }
    },

    // Defensive check in skipExtras
    skipExtras() {
      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        handleExpiredSession(`/bookings/${this.calendarSlug}/home/`);
        return;
      }
      this.trackCheckoutEvent('extras_skipped', {
        source: 'extras_page_skip_action',
      });
      this.trackCheckoutEvent('checkout_option_selected', {
        option_type: 'navigation',
        option_value: 'extras_skip_to_checkout',
      });
      window.location.href = `/bookings/${this.calendarSlug}/checkout/${bookingId}`;
    },

    // Defensive check in proceedToExtras
    async proceedToExtras() {
      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        handleExpiredSession(`/bookings/${this.calendarSlug}/home/`);
        return;
      }
      this.trackCheckoutEvent('checkout_option_selected', {
        option_type: 'navigation',
        option_value: 'cart_to_extras',
      });
      window.location.href = `/bookings/${this.calendarSlug}/extras/${bookingId}`;
    },

    // Defensive check in proceedToCheckout
    async proceedToCheckout() {
      const bookingId = this.getCurrentBookingId();
      if (!bookingId) {
        handleExpiredSession(`/bookings/${this.calendarSlug}/home/`);
        return;
      }
      this.trackCheckoutEvent('checkout_option_selected', {
        option_type: 'navigation',
        option_value: 'cart_to_checkout',
      });
      window.location.href = `/bookings/${this.calendarSlug}/checkout/${bookingId}`;
    },

    // Smart back navigation that handles the booking process flow
    smartBackNavigation() {
      // Check if we came from the booking process (extras or checkout pages)
      const referrer = document.referrer;
      const currentPath = window.location.pathname;

      // If we're on the full cart page and came from extras or checkout, go to store home
      if (
        currentPath.includes('/cart/') &&
        (referrer.includes('/extras/') || referrer.includes('/checkout/'))
      ) {
        window.location.href = `/bookings/${this.calendarSlug}/`;
        return;
      }

      // For all other cases, use browser's back navigation
      window.history.back();
    },

    // Add method to sync selectedExtras with existing booking extras
    syncSelectedExtrasWithBooking() {
      console.log('🔄 Syncing selectedExtras with existing booking extras');

      if (!this.calendarExtras || !this.fullCartBooking) {
        console.log(
          '⏭️ Skipping sync - missing calendarExtras or fullCartBooking',
        );
        return;
      }

      // Initialize dropdown states
      this.selectedExtrasDropdowns = {};
      this.addedExtraOptions = {};

      // Initialize all extras to zero - existing booking extras are shown separately
      this.calendarExtras.forEach((extra) => {
        // Initialize dropdown states for this extra
        this.selectedExtrasDropdowns[extra.id] = '';
        this.addedExtraOptions[extra.id] = [];

        // Always initialize to 0 - existing booking extras are preserved and shown in "Current Extras"
        // This section is only for NEW selections made on the extras page
        this.selectedExtras[extra.id] = 0;

        console.log(
          `📦 Extra ${extra.name}: Initialized for new selections only (existing booking extras shown separately)`,
        );
      });

      console.log('✅ Sync complete. selectedExtras:', this.selectedExtras);
      console.log(
        '✅ Sync complete. addedExtraOptions:',
        this.addedExtraOptions,
      );
    },

    async fetchCalendarExtras(calendarSlug) {
      try {
        const response = await fetch(
          `/bookings/${calendarSlug}/fetch_extras/`,
          {
            headers: {
              'X-CSRFToken': getCSRFToken(),
            },
          },
        );
        const data = await response.json();
        if (data.success) {
          this.calendarExtras = data.extras;

          // Only initialize selectedExtras with zeros if we don't have existing booking data
          if (!this.fullCartBooking) {
            this.calendarExtras.forEach((extra) => {
              this.selectedExtras[extra.id] = 0;
            });
          } else {
            // If we have booking data, sync with existing extras
            this.syncSelectedExtrasWithBooking();
          }
        } else {
          console.error('Failed to fetch extras:', data.error);
        }
      } catch (error) {
        console.error('Error fetching calendar extras:', error);
      }
    },

    hasSelectedExtras() {
      // Check regular selected extras
      const hasRegularExtras = Object.values(this.selectedExtras).some(
        (amount) => amount > 0,
      );

      // Check added dropdown options
      const hasDropdownOptions = Object.values(this.addedExtraOptions).some(
        (options) => options && options.length > 0,
      );

      return hasRegularExtras || hasDropdownOptions;
    },

    getSelectedExtras() {
      const selectedExtras = [];

      // Process each extra
      this.calendarExtras.forEach((extra) => {
        // Add current selection for extras without dropdown (if any)
        const currentQuantity = this.selectedExtras[extra.id] || 0;
        if (
          currentQuantity > 0 &&
          (!extra.dropdown_field_title ||
            !extra.dropdown_options ||
            extra.dropdown_options.length === 0)
        ) {
          selectedExtras.push({
            id: extra.id,
            name: extra.name,
            amount: currentQuantity,
            unit_price: extra.unit_price,
            dropdown_selection: '',
            booking_item_id: null, // New selections from extras page are general extras
          });
        }

        // Add all previously added options for extras with dropdown
        const addedOptions = this.addedExtraOptions[extra.id] || [];
        addedOptions.forEach((addedOption) => {
          selectedExtras.push({
            id: extra.id,
            name: extra.name,
            amount: addedOption.quantity,
            unit_price: extra.unit_price,
            dropdown_selection: addedOption.option,
            booking_item_id: null, // New selections from extras page are general extras
          });
        });
      });

      return selectedExtras;
    },

    addSelectedExtraOption(extraId) {
      const extra = this.calendarExtras.find((e) => e.id === extraId);
      if (!extra) return;

      const selectedOption = this.selectedExtrasDropdowns[extraId];
      if (!selectedOption) {
        console.log('No option selected for extra', extraId);
        return;
      }

      if (!this.addedExtraOptions[extraId]) {
        this.addedExtraOptions[extraId] = [];
      }

      // Check if this option already exists
      const existingOption = this.addedExtraOptions[extraId].find(
        (opt) => opt.option === selectedOption,
      );
      if (existingOption) {
        existingOption.quantity += 1;
      } else {
        this.addedExtraOptions[extraId].push({
          option: selectedOption,
          quantity: 1,
        });
      }

      // Clear the dropdown selection
      this.selectedExtrasDropdowns[extraId] = '';
    },

    handleAutoAddOption(extraId, selectedValue) {
      if (!selectedValue) {
        // Clear the dropdown if empty option selected
        this.selectedExtrasDropdowns[extraId] = '';
        return;
      }

      const extra = this.calendarExtras.find((e) => e.id === extraId);
      if (!extra) return;

      // Update the dropdown value
      this.selectedExtrasDropdowns[extraId] = selectedValue;

      // Automatically add the selected option
      if (!this.addedExtraOptions[extraId]) {
        this.addedExtraOptions[extraId] = [];
      }

      // Check if this option already exists
      const existingOption = this.addedExtraOptions[extraId].find(
        (opt) => opt.option === selectedValue,
      );
      if (existingOption) {
        // Increment existing option quantity
        existingOption.quantity += 1;
      } else {
        // Add new option with quantity 1
        this.addedExtraOptions[extraId].push({
          option: selectedValue,
          quantity: 1,
        });
      }

      // Clear the dropdown selection for next selection
      this.selectedExtrasDropdowns[extraId] = '';

      console.log('Auto-added option:', selectedValue, 'for extra', extraId);
      console.log('Current added options:', this.addedExtraOptions[extraId]);
    },

    updateAddedExtraQuantity(extraId, index, newQuantity) {
      if (
        !this.addedExtraOptions[extraId] ||
        !this.addedExtraOptions[extraId][index]
      )
        return;

      if (newQuantity <= 0) {
        this.addedExtraOptions[extraId].splice(index, 1);
      } else {
        this.addedExtraOptions[extraId][index].quantity = parseInt(newQuantity);
      }
    },

    removeAddedExtraOption(extraId, index) {
      if (!this.addedExtraOptions[extraId]) return;
      this.addedExtraOptions[extraId].splice(index, 1);
    },

    getAddedExtraOptions(extraId) {
      return this.addedExtraOptions[extraId] || [];
    },

    getTotalWithExtras() {
      if (!this.fullCartBooking) return 0;

      // Start with booking items total (use all_booking_items to include all items in packages)
      const bookingItemsBaseTotal =
        this.fullCartBooking.all_booking_items?.reduce(
          (sum, item) => sum + parseFloat(item.final_price || 0),
          0,
        ) || 0;

      // Package-defined extras are visually treated as booking items.
      const packageComponentExtrasTotal =
        this.fullCartBooking.booking_extras
          ?.filter((extra) => extra.source_type === 'PACKAGE_COMPONENT')
          ?.reduce(
            (sum, extra) => sum + parseFloat(extra.final_price || 0),
            0,
          ) || 0;

      const bookingItemsTotal =
        bookingItemsBaseTotal + packageComponentExtrasTotal;

      // Only customer-managed existing extras should be counted under Extras/Add-ons.
      const existingExtrasTotal =
        this.fullCartBooking.booking_extras?.reduce((sum, extra) => {
          if (extra.source_type === 'PACKAGE_COMPONENT') return sum;
          return sum + parseFloat(extra.final_price || 0);
        }, 0) || 0;

      // Add new selections from the extras page (shown in "Additional Extras")
      const newExtrasTotal =
        this.calendarExtras?.reduce((sum, extra) => {
          let extraTotal = 0;

          if (
            extra.dropdown_field_title &&
            extra.dropdown_options &&
            extra.dropdown_options.length > 0
          ) {
            // For dropdown extras, sum up all added options
            const addedOptions = this.addedExtraOptions[extra.id] || [];
            extraTotal = addedOptions.reduce(
              (optionSum, option) =>
                optionSum + option.quantity * parseFloat(extra.unit_price),
              0,
            );
          } else {
            // For regular extras, use selectedExtras
            const quantity = this.selectedExtras[extra.id] || 0;
            extraTotal = quantity * parseFloat(extra.unit_price);
          }

          return sum + extraTotal;
        }, 0) || 0;

      const total = bookingItemsTotal + existingExtrasTotal + newExtrasTotal;

      console.log('💰 Total calculation:', {
        bookingItemsTotal,
        packageComponentExtrasTotal,
        existingExtrasTotal,
        newExtrasTotal,
        total,
        existingExtras: this.fullCartBooking.booking_extras,
      });

      return total;
    },

    // =====================================================================
    // COUPON CODE METHODS
    // =====================================================================

    async applyCoupon() {
      if (!this.couponCode.trim()) {
        this.couponError = 'Please enter a coupon code';
        return;
      }

      // Front-end guard: prevent applying if a coupon is already active
      if (this.appliedCoupon) {
        this.couponError =
          'A coupon is already applied. Remove it first to use another.';
        return;
      }

      this.isApplyingCoupon = true;
      this.couponError = '';

      try {
        const response = await fetch('/bookings_app/apply-coupon/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking?.id,
            coupon_code: this.couponCode.trim(),
            session_id: this.getSessionId(),
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.appliedCoupon = {
            discount_name: data.discount_name,
            total_saved: data.total_saved,
            coupon_code: data.coupon_code,
            discount_id: data.discount_id || null,
          };
          this.couponCode = '';
          this.clearPayPalPendingOrder();
          // Refresh cart data so total_price and applied_discounts update in the UI
          await this.fetchCartData(this.editModeBooking);
          // Refresh Stripe payment section with new amount (no full page reload)
          if (this.bookingType === 'instant') {
            const deposit = this.selectedDeposit ?? 100;
            await this.updatePaymentIntent(deposit);
          }
          this.trackCheckoutEvent('apply_coupon', {
            coupon_code: data.coupon_code || null,
            discount_name: data.discount_name || null,
            total_saved: this.toTrackingNumber(data.total_saved, 0),
          });
        } else {
          this.couponError = data.error || 'Failed to apply coupon code';
        }
      } catch (error) {
        console.error('Error applying coupon:', error);
        this.couponError = 'An error occurred. Please try again.';
      } finally {
        this.isApplyingCoupon = false;
      }
    },

    async removeCoupon() {
      if (!this.appliedCoupon) return;

      try {
        const response = await fetch('/bookings_app/remove-coupon/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking?.id,
            discount_id: this.appliedCoupon.discount_id,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          const removedCouponCode = this.appliedCoupon?.coupon_code || null;
          this.appliedCoupon = null;
          this.clearPayPalPendingOrder();
          // Refresh cart data
          await this.fetchCartData(this.editModeBooking);
          // Refresh Stripe payment section with new amount (no full page reload)
          if (this.bookingType === 'instant') {
            const deposit = this.selectedDeposit ?? 100;
            await this.updatePaymentIntent(deposit);
          }
          this.trackCheckoutEvent('remove_coupon', {
            coupon_code: removedCouponCode,
          });
        }
      } catch (error) {
        console.error('Error removing coupon:', error);
      }
    },

    // =====================================================================
    // PARTNER ACCESS CODE METHODS
    // =====================================================================

    partnerCheckoutIsInstant() {
      if (!this.authenticatedPartner) {
        return this.bookingType === 'instant';
      }

      if (this.authenticatedPartner?.access_code_always_instant_book) {
        return true;
      }

      return this.bookingType === 'instant';
    },

    getPartnerPaidAmount() {
      if (!this.authenticatedPartner) return 0;
      const totalPrice = Number(this.fullCartBooking?.total_price || 0);
      const paymentMode = this.authenticatedPartner?.payment_mode;

      if (paymentMode === 'FULL_PAYMENT') {
        return totalPrice;
      }

      if (paymentMode === 'DEPOSIT') {
        const commissionRate = Number(
          this.authenticatedPartner?.commission_rate || 0,
        );
        return (totalPrice * commissionRate) / 100;
      }

      return 0;
    },

    getPartnerAmountDueToBusiness() {
      const totalPrice = Number(this.fullCartBooking?.total_price || 0);
      const paidToPartner = this.getPartnerPaidAmount();
      return Math.max(0, totalPrice - paidToPartner);
    },

    async partnerLogin() {
      if (!this.partnerCode.trim()) {
        this.partnerLoginError = 'Please enter a partner code';
        return;
      }

      this.isPartnerLoggingIn = true;
      this.partnerLoginError = '';

      try {
        const response = await fetch('/bookings_app/partner-login/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking?.id,
            partner_code: this.partnerCode.trim(),
            calendar_id: this.calendarId,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.authenticatedPartner = data.partner;
          if (this.authenticatedPartner?.access_code_always_instant_book) {
            this.handleBookingTypeSelection('instant');
          }
          this.partnerCode = '';
          // Refresh cart data to get updated prices (partner discounts applied)
          await this.fetchCartData(this.editModeBooking);
          // No page reload needed: partner bookings bypass Stripe entirely.
          // The deposit options and Stripe form are hidden by x-show when
          // authenticatedPartner is set, and confirmPartnerBooking() handles
          // submission without Stripe.
        } else {
          this.partnerLoginError = data.error || 'Invalid partner code';
        }
      } catch (error) {
        console.error('Error in partner login:', error);
        this.partnerLoginError = 'An error occurred. Please try again.';
      } finally {
        this.isPartnerLoggingIn = false;
      }
    },

    async partnerLogout() {
      if (!this.authenticatedPartner) return;

      try {
        const response = await fetch('/bookings_app/partner-logout/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking?.id,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          this.authenticatedPartner = null;
          this.partnerBookingCode = '';
          // Refresh cart data
          await this.fetchCartData(this.editModeBooking);
          // Reload to restore Stripe PaymentIntent
          window.location.reload();
        }
      } catch (error) {
        console.error('Error in partner logout:', error);
      }
    },

    async confirmPartnerBooking() {
      if (this.isLoading) {
        return;
      }

      // Validate guest info
      if (!this.validateGuestInfo()) {
        return;
      }

      if (!this.termsAccepted) {
        this.setSubmitMessage(
          'Please accept the Terms and Conditions',
          'warning',
        );
        return;
      }

      this.isLoading = true;
      this.trackCheckoutEvent('checkout_submit_attempt', {
        submit_mode: 'partner_bypass',
      });

      try {
        // Store guest info first
        const storeGuestInfoResponse = await fetch(
          '/bookings_app/store-guest-info/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              booking_id: this.editModeBooking.id,
              guest_info: this.getGuestInfoPayload(),
              custom_fields: this.customFields,
              booking_message: this.bookingMessage || '',
              session_id: this.getSessionId(),
              calendar_id: this.calendarId,
            }),
          },
        );

        if (!storeGuestInfoResponse.ok) {
          const errorData = await storeGuestInfoResponse.json();
          throw new Error(
            this.extractErrorMessage(
              errorData,
              'Failed to store guest information',
            ),
          );
        }

        // Confirm via partner endpoint
        const response = await fetch('/bookings_app/confirm-partner-booking/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking.id,
            guest_info: this.getGuestInfoPayload(),
            custom_fields: this.customFields,
            message: this.bookingMessage || '',
            partner_booking_code: this.partnerBookingCode || '',
            access_code_label:
              this.authenticatedPartner?.access_code_label || '',
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          if (data.mode === 'request') {
            window.location.href = `/bookings/${this.calendarSlug}/request-confirmation/${data.booking_id}/`;
          } else {
            window.location.href = `/bookings/${this.calendarSlug}/payment-confirmation/${data.booking_id}/?partner=true`;
          }
          return;
        } else {
          this.setSubmitMessage(
            data.error || 'Failed to confirm booking',
            'warning',
          );
        }
      } catch (error) {
        console.error('Error confirming partner booking:', error);
        this.setSubmitMessage(error.message || 'An error occurred', 'warning');
      } finally {
        this.isLoading = false;
      }
    },

    // =====================================================================
    // ADMIN BYPASS METHOD
    // =====================================================================

    async confirmAdminBooking() {
      if (this.isLoading) {
        return;
      }

      // Validate guest info
      if (!this.validateGuestInfo()) {
        return;
      }

      if (!this.termsAccepted) {
        this.setSubmitMessage(
          'Please accept the Terms and Conditions',
          'warning',
        );
        return;
      }

      this.isLoading = true;
      this.trackCheckoutEvent('checkout_submit_attempt', {
        submit_mode: 'admin_bypass',
      });

      try {
        // Store guest info first
        const storeGuestInfoResponse = await fetch(
          '/bookings_app/store-guest-info/',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': getCSRFToken(),
            },
            body: JSON.stringify({
              booking_id: this.editModeBooking.id,
              guest_info: this.getGuestInfoPayload(),
              custom_fields: this.customFields,
              booking_message: this.bookingMessage || '',
              session_id: this.getSessionId(),
              calendar_id: this.calendarId,
            }),
          },
        );

        if (!storeGuestInfoResponse.ok) {
          const errorData = await storeGuestInfoResponse.json();
          throw new Error(
            this.extractErrorMessage(
              errorData,
              'Failed to store guest information',
            ),
          );
        }

        // Confirm via admin endpoint
        const response = await fetch('/bookings_app/confirm-admin-booking/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCSRFToken(),
          },
          body: JSON.stringify({
            booking_id: this.editModeBooking.id,
            guest_info: this.getGuestInfoPayload(),
            custom_fields: this.customFields,
            message: this.bookingMessage || '',
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          window.location.href = `/bookings/${this.calendarSlug}/payment-confirmation/${data.booking_id}/?admin=true`;
          return;
        } else {
          this.setSubmitMessage(
            data.error || 'Failed to confirm booking',
            'warning',
          );
        }
      } catch (error) {
        console.error('Error confirming admin booking:', error);
        this.setSubmitMessage(error.message || 'An error occurred', 'warning');
      } finally {
        this.isLoading = false;
      }
    },

    async confirmBooking() {
      if (this.isLoading) {
        return;
      }

      // Validate guest info
      if (!this.validateGuestInfo()) {
        return;
      }

      // Check terms acceptance
      if (!this.termsAccepted) {
        this.setSubmitMessage(
          'Please accept the Terms and Conditions',
          'warning',
        );
        return;
      }

      this.isLoading = true;

      try {
        const currentSessionId = this.getSessionId();

        // Use the store's selectedDeposit value directly
        const depositPercentage = this.selectedDeposit;

        // Validate required data exists
        if (!this.editModeBooking?.id || !currentSessionId) {
          console.error('Missing required data for payment confirmation');
          window.location.href = `/bookings/${this.calendarSlug}/home`;
          return;
        }

        if (this.bookingType === 'instant') {
          if (this.selectedPaymentProvider === 'paypal') {
            await this.startPayPalRedirectCheckout();
            return;
          }

          if (this.selectedPaymentProvider !== 'stripe') {
            this.setSubmitMessage(
              'Unsupported payment provider selected.',
              'warning',
            );
            return;
          }

          // Validate guest information
          if (
            !this.guestInfo.email ||
            !this.guestInfo.firstName ||
            !this.guestInfo.lastName
          ) {
            this.setSubmitMessage(
              'Please fill in all required information',
              'warning',
            );
            return;
          }

          // Consider adding email format validation before submission
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(this.guestInfo.email)) {
            this.setSubmitMessage(
              'Please enter a valid email address',
              'warning',
            );
            return;
          }

          if (!this.elements || !this.stripe) {
            console.error('Stripe not properly initialized');
            return;
          }

          try {
            this.trackCheckoutEvent('checkout_submit_attempt', {
              submit_mode: 'instant',
              payment_provider: 'stripe',
            });

            const isPaymentElementReady =
              await this.ensureStripePaymentElementReadyForSubmit();
            if (!isPaymentElementReady) {
              this.setSubmitMessage(
                'Payment form is still loading. Please wait a moment and try again.',
                'warning',
              );
              return;
            }

            // First validate the payment element
            const { error: validationError } = await this.elements.submit();

            if (validationError) {
              this.showPaymentError(validationError.message);
              return;
            }

            // Store guest info and deposit percentage on the server before proceeding with payment
            await this.persistGuestInfoForCheckout(
              currentSessionId,
              depositPercentage,
            );

            // Now proceed with payment
            const { error } = await this.stripe.confirmPayment({
              elements: this.elements,
              confirmParams: {
                return_url: `${window.location.origin}/bookings/${this.calendarSlug}/payment-confirmation/${this.editModeBooking.id}/?deposit=${depositPercentage}`,
                payment_method_data: {
                  billing_details: {
                    name: `${this.guestInfo.firstName} ${this.guestInfo.lastName}`,
                    email: this.guestInfo.email,
                  },
                },
              },
            });

            if (error) {
              this.showPaymentError(error.message);
              return;
            }
          } catch (e) {
            this.showPaymentError(
              e.message || 'An error occurred during payment processing.',
            );
          }
        } else {
          // Request booking flow
          try {
            this.trackCheckoutEvent('checkout_submit_attempt', {
              submit_mode: 'request',
            });
            const response = await fetch('/bookings_app/submit-request/', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken(),
              },
              body: JSON.stringify({
                booking_id: this.editModeBooking.id,
                guest_info: this.getGuestInfoPayload(),
                custom_fields: this.customFields,
                message: this.bookingMessage || '',
                session_id: currentSessionId,
                calendar_id: this.calendarId,
              }),
            });

            const data = await response.json();

            if (response.ok) {
              // Show success message and redirect to confirmation page
              window.location.href = `/bookings/${this.calendarSlug}/request-confirmation/${data.booking_id}/`;
              return;
            } else {
              // Extract the error message properly
              const errorMessage = this.extractErrorMessage(
                data,
                'Failed to submit booking request',
              );
              throw new Error(errorMessage);
            }
          } catch (error) {
            console.error('Error submitting booking request:', error);

            // Extract proper error message
            let displayMessage =
              'Failed to submit booking request. Please try again.';
            if (error.message && error.message !== '[object Object]') {
              displayMessage = error.message;
            }

            if (this.toastStore && this.toastStore.addToast) {
              this.toastStore.addToast(displayMessage, 'danger');
            }
          }
        }
      } catch (error) {
        console.error('Error confirming booking:', error);

        // Extract proper error message
        let displayMessage = 'An error occurred while processing your booking';
        if (error.message && error.message !== '[object Object]') {
          displayMessage = error.message;
        }

        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(displayMessage, 'danger');
        }
      } finally {
        this.isLoading = false;
      }
    },
    closePaymentUnavailableModal() {
      console.log('Closing modal and switching to request booking');
      this.showPaymentUnavailableModal = false;
      this.handleBookingTypeSelection('request');
    },
    getAllSessionSources() {
      return {
        sessionStorage: sessionStorage.getItem('bookingSessionId'),
        localStorage: localStorage.getItem('bookingSessionId'),
        cookie: this.getCookie('bookingSessionId'),
      };
    },
    // Helper method to safely show payment errors
    showPaymentError(message) {
      // Try to show in the payment message element
      const messageDiv = document.getElementById('payment-message');
      if (messageDiv) {
        messageDiv.textContent = message;
        messageDiv.classList.remove('hidden');
      } else {
        // Fallback to toast if element not found
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast(message, 'danger');
        }
      }
      this.trackCheckoutEvent('payment_failed', {
        payment_provider: this.selectedPaymentProvider || null,
        error_message: message || '',
      });
      if (this.selectedPaymentProvider === 'stripe') {
        this.trackCheckoutEvent('stripe_error', {
          error_message: message || '',
        });
      }
      if (this.selectedPaymentProvider === 'paypal') {
        this.trackCheckoutEvent('paypal_error', {
          error_message: message || '',
        });
      }
    },
    // Add initialization for deposit options
    initializeDeposit(depositOptions, defaultDeposit, totalAmount) {
      this.depositOptions = depositOptions;
      this.selectedDeposit = defaultDeposit;
      this.totalAmount = totalAmount;
    },
    // Add helper method for price formatting
    formatPrice(amount, currency) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency,
      }).format(amount);
    },
    async updatePaymentIntent(depositPercentage) {
      if (
        this.calendarMode === 'RO' ||
        !this.stripe ||
        this.selectedPaymentProvider !== 'stripe'
      )
        return;

      try {
        // Best-effort attribution sync before Stripe PI refresh (GET cannot carry body).
        try {
          if (
            window.HightideTracking &&
            typeof window.HightideTracking.syncAttributionToBooking ===
              'function'
          ) {
            await window.HightideTracking.syncAttributionToBooking({
              bookingId: this.editModeBooking && this.editModeBooking.id,
              cartSessionKey: this.getSessionId && this.getSessionId(),
            });
          }
        } catch (attributionError) {
          console.warn(
            'Non-fatal: attribution sync before payment intent update failed',
            attributionError,
          );
        }

        // Get new payment intent for selected deposit
        const response = await fetch(
          `/bookings/${this.calendarSlug}/checkout/${this.editModeBooking.id}/?deposit=${depositPercentage}&provider=${this.selectedPaymentProvider}`,
          {
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
            },
          },
        );

        const data = await response.json();

        if (response.ok && data.client_secret) {
          this.clientSecret = data.client_secret;
          await this.initializePaymentElement(true); // true indicates reinitialize
          return;
        }

        console.error(
          'Checkout PaymentIntent unavailable:',
          data && data.error,
        );
        this.showPaymentUnavailableModal = true;
      } catch (error) {
        console.error('Error updating payment intent:', error);
        this.showPaymentUnavailableModal = true;
        if (this.toastStore && this.toastStore.addToast) {
          this.toastStore.addToast('Failed to update payment amount', 'danger');
        }
      }
    },
    async handleDepositChange(value) {
      const newValue = Number(value);
      if (this.selectedDeposit !== newValue) {
        this.selectedDeposit = newValue;
        this.trackCheckoutEvent('checkout_option_selected', {
          option_type: 'deposit_percentage',
          option_value: newValue,
        });
        if (this.selectedPaymentProvider === 'stripe') {
          await this.updatePaymentIntent(newValue);
        }
      }
    },
    openTermsModal() {
      console.log('Opening modal with terms:', this.termsContent);
      this.showTermsModal = true;
    },
    closeTermsModal() {
      this.showTermsModal = false;
    },
    isGuestEmailRequired() {
      if (!this.authenticatedPartner) return true;
      return Boolean(this.authenticatedPartner?.guest_email_required);
    },
    getGuestEmailLabel() {
      return this.authenticatedPartner ? 'Guest Email' : 'Email';
    },
    getNormalizedGuestPhoneNumber() {
      const rawInput = String(this.guestInfo.phone_number || '').trim();
      if (!rawInput) {
        return '';
      }

      const dialCode = String(this.guestInfo.phone_country_code || '').replace(
        /[^\d+]/g,
        '',
      );
      const nationalDigits = rawInput.replace(/\D/g, '');
      if (!nationalDigits) {
        return '';
      }

      const candidate = rawInput.startsWith('+')
        ? `+${rawInput.replace(/\D/g, '')}`
        : `${dialCode}${nationalDigits}`;
      const parsedPhone = parsePhoneNumberFromString(candidate);

      if (parsedPhone?.isValid()) {
        return parsedPhone.number;
      }

      return candidate.replace(/[^\d+]/g, '');
    },
    getGuestInfoPayload() {
      return {
        ...this.guestInfo,
        phone_number: this.getNormalizedGuestPhoneNumber(),
      };
    },
    validateGuestInfo() {
      const firstName = String(this.guestInfo.firstName || '').trim();
      const lastName = String(this.guestInfo.lastName || '').trim();
      const email = String(this.guestInfo.email || '').trim();
      const normalizedEmail = email.toLowerCase();

      if (!firstName || !lastName) {
        this.setSubmitMessage(
          'Please fill in all required information',
          'warning',
        );
        return false;
      }

      const emailRequired = this.isGuestEmailRequired();

      if (emailRequired && !email) {
        this.setSubmitMessage(
          'Guest email is required for this booking',
          'warning',
        );
        return false;
      }

      if (email) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.setSubmitMessage(
            'Please enter a valid email address',
            'warning',
          );
          return false;
        }
      }

      if (this.authenticatedPartner && email) {
        const knownEmails = Array.isArray(
          this.authenticatedPartner.known_emails,
        )
          ? this.authenticatedPartner.known_emails
              .map((value) =>
                String(value || '')
                  .trim()
                  .toLowerCase(),
              )
              .filter(Boolean)
          : [];
        if (knownEmails.includes(normalizedEmail)) {
          this.setSubmitMessage(
            "Please enter the guest email address, not the partner's own email.",
            'warning',
          );
          return false;
        }
      }

      return true;
    },
    setExpiredMessage() {
      // Check if we've recently shown a message
      const lastShown = localStorage.getItem('lastExpiredMessageTime');
      const now = Date.now();

      if (lastShown && now - parseInt(lastShown) < 5000) {
        // 5 second cooldown
        console.log('Skipping expired message - too soon since last one');
        return;
      }

      localStorage.setItem(
        'sessionExpiredMessage',
        'Your booking session has expired...',
      );
      localStorage.setItem('lastExpiredMessageTime', now.toString());
    },
    checkExpiredMessage() {
      const expiredMessage = localStorage.getItem('sessionExpiredMessage');
      if (!expiredMessage) return;

      try {
        // Only remove after we're sure we can show it
        this.showExpiredModal = true;
        localStorage.removeItem('sessionExpiredMessage');
        console.log('Expired message shown and cleared');
      } catch (error) {
        console.error('Error showing expired message:', error);
        // If something goes wrong, ensure message is cleared
        localStorage.removeItem('sessionExpiredMessage');
      }
    },
    setSubmitMessage(message, type = 'warning') {
      this.submitMessage = message;
      this.messageType = type;
      if (!message || type !== 'warning') {
        return;
      }
      const normalizedMessage = String(message).toLowerCase();
      this.trackCheckoutEvent('validation_error', {
        message: message,
      });
      if (normalizedMessage.includes('availability')) {
        this.trackCheckoutEvent('availability_changed', { message: message });
      }
      if (normalizedMessage.includes('conflict')) {
        this.trackCheckoutEvent('booking_conflict', { message: message });
      }
      if (
        normalizedMessage.includes('payment') ||
        normalizedMessage.includes('card')
      ) {
        this.trackCheckoutEvent('payment_failed', {
          payment_provider: this.selectedPaymentProvider || null,
          error_message: message,
        });
      }
    },
    clearSubmitMessage() {
      this.submitMessage = '';
      this.messageType = '';
    },

    // Method to sync rate set extras with cart store
    async syncRateSetExtras(rateSetExtras) {
      if (!rateSetExtras || rateSetExtras.length === 0) {
        return;
      }

      console.log('🔄 Syncing rate set extras with cart store:', rateSetExtras);
      console.log('📊 Current selectedExtras before sync:', {
        ...this.selectedExtras,
      });

      // Check if we have an existing booking and initialize cart store if needed
      if (!this.editModeBooking && this.sessionId && this.calendarId) {
        console.log(
          '🔍 No editModeBooking found, checking for existing booking...',
        );
        try {
          await this.checkExistingBooking(this.sessionId, this.calendarId);
        } catch (error) {
          console.error('❌ Failed to check existing booking:', error);
        }
      }

      // Ensure calendar extras are loaded first
      if (!this.calendarExtras || this.calendarExtras.length === 0) {
        if (this.calendarSlug) {
          await this.fetchCalendarExtras(this.calendarSlug);
        }
      }

      // Load existing booking data to get current extras
      if (this.editModeBooking && this.editModeBooking.id) {
        try {
          console.log(
            '🔄 Loading existing booking data to sync current extras...',
          );
          console.log('📋 Current editModeBooking:', this.editModeBooking);
          const response = await fetch(
            `/bookings/${this.calendarSlug}/cart/${this.editModeBooking.id}/details/`,
          );
          if (response.ok) {
            const bookingData = await response.json();
            console.log('📋 Loaded booking data:', bookingData);
            console.log(
              '📋 Booking extras from API:',
              bookingData.booking.booking_extras,
            );
            this.fullCartBooking = bookingData.booking;
            // Sync existing extras with current state
            this.syncSelectedExtrasWithBooking();
          } else {
            console.error(
              '❌ Failed to load booking data, status:',
              response.status,
            );
          }
        } catch (error) {
          console.error('❌ Failed to load existing booking data:', error);
        }
      } else {
        console.log('⚠️ No editModeBooking or booking ID found');
        console.log('📋 editModeBooking:', this.editModeBooking);
      }

      // Initialize selectedExtras if still empty
      if (
        !this.selectedExtras ||
        Object.keys(this.selectedExtras).length === 0
      ) {
        this.selectedExtras = {};
        this.calendarExtras.forEach((extra) => {
          this.selectedExtras[extra.id] = 0;
        });
      }

      // Update selectedExtras with rate set selections
      rateSetExtras.forEach((selectedExtra) => {
        if (this.selectedExtras) {
          // Add to existing cart extras (don't overwrite)
          const currentAmount =
            this.selectedExtras[selectedExtra.extra_id] || 0;
          this.selectedExtras[selectedExtra.extra_id] =
            currentAmount + selectedExtra.quantity;
          console.log(
            `📦 Extra ${selectedExtra.extra_id}: ${currentAmount} + ${
              selectedExtra.quantity
            } = ${this.selectedExtras[selectedExtra.extra_id]}`,
          );
        }
      });

      // Save the updated extras to the booking
      if (this.saveExtras) {
        await this.saveExtras();
      }

      console.log('📊 Final selectedExtras after sync:', {
        ...this.selectedExtras,
      });
      console.log('✅ Rate set extras synced successfully');
    },
  });
}
